<template>
  <div class="prompt-enhancement-manager">
    <div class="header-section">
      <h2>🔧 Check Definition Builder</h2>
      <p class="description">
        Create and manage comprehensive check definitions with AI-powered prompts for 
        automated document analysis, compliance verification, and insurance review.
      </p>
    </div>

    <!-- STEP 1: SELECT OR CREATE DEFINITION -->
    <div class="workflow-section step-1-section">
      <div class="step-header">
        <div class="step-number">1</div>
        <div class="step-info">
          <h3>Select or Create a Check Definition</h3>
          <p>Choose an existing definition to work with, or create a new one to get started.</p>
        </div>
      </div>
      
      <div class="definition-selector">
        <div class="selector-row">
          <div class="selector-main">
            <select 
              v-model="selectedCheckDefinition" 
              @change="onCheckDefinitionChange"
              class="form-control definition-dropdown" 
              :disabled="availableCheckDefinitions.length === 0"
            >
              <option value="">{{ availableCheckDefinitions.length > 0 ? '-- Select a Check Definition --' : 'No definitions available' }}</option>
              <option 
                v-for="def in availableCheckDefinitions" 
                :key="def.id" 
                :value="def.id"
              >
                {{ def.name }} ({{ def.project_type || 'General' }})
              </option>
            </select>
          </div>
          
          <div class="selector-actions">
            <button 
              @click="openCreateDefinitionModal"
              class="btn btn-success"
              title="Create a new check definition"
            >
              ➕ New Definition
            </button>
            
            <button 
              v-if="selectedCheckDefinition"
              @click="openCopyDefinitionModal"
              class="btn btn-outline"
              title="Copy this check definition and all its requirements"
            >
              📄 Copy
            </button>
          </div>
        </div>
      </div>

      <!-- Selected Check Definition Info Card -->
      <div v-if="selectedCheckDefinition && selectedCheckDefinitionData" class="selected-definition-card">
        <div class="definition-header">
          <div class="definition-icon">📋</div>
          <div class="definition-details">
            <h4>{{ selectedCheckDefinitionData.name }}</h4>
            <p class="definition-description">{{ selectedCheckDefinitionData.description || 'No description available' }}</p>
          </div>
        </div>
        <div class="definition-meta">
          <span class="meta-badge type">{{ selectedCheckDefinitionData.project_type || 'General' }}</span>
          <span class="meta-badge prompts" v-if="definitionPromptCount !== null">
            {{ definitionPromptCount }} prompts
          </span>
          <span class="meta-badge app-type" v-if="selectedCheckDefinitionData.check_application_type">
            {{ formatApplicationType(selectedCheckDefinitionData.check_application_type) }}
          </span>
        </div>
      </div>
      
      <!-- Empty State when no definition selected -->
      <div v-else class="empty-definition-state">
        <div class="empty-icon">📂</div>
        <p>Select a check definition above to view and manage its prompts, or create a new one to get started.</p>
      </div>
    </div>

      <!-- Analysis Results -->
    <!-- STEP 2: ADD PROMPTS (Only shown when definition is selected) -->
    <div v-if="selectedCheckDefinition" class="workflow-section step-2-section">
      <div class="step-header">
        <div class="step-number">2</div>
        <div class="step-info">
          <h3>Add Prompts to This Definition</h3>
          <p>Create new prompts to add compliance checks to your definition.</p>
        </div>
      </div>

      <!-- Quick Create Form - Always Visible When Definition Selected -->
      <div class="quick-create-inline">
        <div class="quick-create-header-inline">
          <h4>🚀 Quick Create</h4>
          <span class="helper-text">Create a simple prompt - AI can enhance it later</span>
        </div>
        
        <div class="quick-form-grid">
          <div class="form-group">
            <label>Prompt Name *</label>
            <input 
              v-model="quickPrompt.name" 
              type="text" 
              class="form-control"
              placeholder="e.g., Workers Comp Exclusion Check"
            >
          </div>
          
          <div class="form-group">
            <label>Type *</label>
            <select v-model="quickPrompt.type" class="form-control">
              <option value="">Select Type</option>
              <option value="text_match">Text Match</option>
              <option value="coverage_amount">Coverage Amount</option>
              <option value="date_check">Date Check</option>
              <option value="entity_verification">Entity Verification</option>
              <option value="checkbox_verification">Checkbox/Y-N Verification</option>
              <option value="custom">Custom</option>
            </select>
          </div>
          
          <div class="form-group">
            <label>Severity</label>
            <select v-model="quickPrompt.severity" class="form-control">
              <option value="LOW">Low</option>
              <option value="MEDIUM">Medium</option>
              <option value="HIGH">High</option>
              <option value="CRITICAL">Critical</option>
            </select>
          </div>
        </div>
        
        <div class="form-group">
          <label>What should this prompt check? *</label>
          <textarea 
            v-model="quickPrompt.prompt_template" 
            class="form-control"
            rows="2"
            placeholder="e.g., Check if owners are excluded from workers compensation coverage"
          ></textarea>
        </div>
        
        <div class="quick-form-actions">
          <button 
            @click="createSimplePromptForSelectedDefinition" 
            :disabled="!canCreateQuickPromptForSelected || loading" 
            class="btn btn-success"
          >
            <i class="fas fa-plus"></i> Create Prompt
          </button>
          <button 
            @click="createAndEnhancePromptForSelectedDefinition" 
            :disabled="!canCreateQuickPromptForSelected || loading" 
            class="btn btn-primary"
          >
            <i class="fas fa-magic"></i> Create & Enhance with AI
          </button>
          
          <div class="bulk-actions-group">
            <button @click="showBulkPromptCreator = true" class="btn btn-outline">
              📝 Bulk Create
            </button>
            <button @click="showBulkImportModal = true" class="btn btn-outline">
              📊 Import CSV
            </button>
            <button @click="exportPromptsForDefinition" :disabled="loading" class="btn btn-outline">
              📤 Export
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- STEP 3: VIEW & MANAGE PROMPTS IN THIS DEFINITION -->
    <div v-if="selectedCheckDefinition" class="workflow-section step-3-section">
      <div class="step-header">
        <div class="step-number">3</div>
        <div class="step-info">
          <h3>Prompts in This Definition</h3>
          <p>View, edit, and manage all prompts belonging to the selected definition.</p>
        </div>
        <div class="step-actions">
          <button 
            @click="loadPromptsForSelectedDefinition" 
            :disabled="loading"
            class="btn btn-primary btn-sm"
          >
            {{ loading ? 'Loading...' : '🔄 Refresh' }}
          </button>
        </div>
      </div>

      <!-- Prompts List for Selected Definition -->
      <div class="definition-prompts-container">
        <!-- Search and Filter Controls -->
        <div class="prompts-controls">
          <input 
            v-model="definitionPromptsSearch" 
            placeholder="Search prompts..." 
            class="form-control search-input"
          >
          <select v-model="definitionPromptsTypeFilter" class="form-control filter-select">
            <option value="">All Types</option>
            <option value="text_match">Text Match</option>
            <option value="coverage_amount">Coverage Amount</option>
            <option value="date_check">Date Check</option>
            <option value="entity_verification">Entity Verification</option>
            <option value="checkbox_verification">Checkbox/Y-N</option>
            <option value="custom">Custom</option>
          </select>
          <div class="inactive-toggle-inline">
            <label class="toggle-switch">
              <input type="checkbox" v-model="showInactivePromptsInDefinition">
              <span class="toggle-slider"></span>
            </label>
            <span class="toggle-label">Show Inactive</span>
          </div>
        </div>

        <!-- Loading State -->
        <div v-if="loadingDefinitionPrompts" class="loading-prompts">
          <div class="spinner"></div>
          <span>Loading prompts...</span>
        </div>

        <!-- Empty State -->
        <div v-else-if="filteredDefinitionPrompts.length === 0 && !loadingDefinitionPrompts" class="empty-prompts-state">
          <div class="empty-icon">📝</div>
          <h4>No prompts yet</h4>
          <p>Use the Quick Create form above to add your first prompt to this definition.</p>
        </div>

        <!-- Prompts Table -->
        <div v-else class="prompts-table-container">
          <table class="prompts-table">
            <thead>
              <tr>
                <th class="col-active">Active</th>
                <th class="col-name">Name / Code</th>
                <th class="col-type">Type</th>
                <th class="col-severity">Severity</th>
                <th class="col-status">Status</th>
                <th class="col-actions">Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr 
                v-for="prompt in filteredDefinitionPrompts" 
                :key="prompt.id"
                class="prompt-row-item"
                :class="{ 'inactive-row': !prompt.is_active, 'enhanced-row': prompt.has_enhanced_version }"
              >
                <td class="col-active">
                  <label class="toggle-switch compact">
                    <input 
                      type="checkbox" 
                      :checked="prompt.is_active" 
                      @change="toggleRequirementActive(prompt)"
                      :disabled="prompt.toggling"
                    >
                    <span class="toggle-slider"></span>
                  </label>
                </td>
                <td class="col-name">
                  <div class="name-cell-content">
                    <strong>{{ prompt.name }}</strong>
                    <code class="code-badge">{{ prompt.code }}</code>
                  </div>
                </td>
                <td class="col-type">
                  <span class="type-badge-sm">{{ formatType(prompt.type) }}</span>
                </td>
                <td class="col-severity">
                  <span class="severity-badge-sm" :class="prompt.severity?.toLowerCase()">
                    {{ prompt.severity }}
                  </span>
                </td>
                <td class="col-status">
                  <span v-if="prompt.has_enhanced_version" class="status-enhanced">✨ Enhanced</span>
                  <span v-else class="status-basic">📝 Basic</span>
                </td>
                <td class="col-actions">
                  <div class="action-buttons">
                    <button @click="editPrompt(prompt)" class="btn btn-xs btn-primary" title="Edit">
                      ✏️
                    </button>
                    <button @click="previewEnhancement(prompt.id)" class="btn btn-xs btn-outline" title="Preview/Enhance">
                      ✨
                    </button>
                    <button @click="duplicatePrompt(prompt)" class="btn btn-xs btn-outline" title="Duplicate">
                      📄
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
          
          <div class="prompts-summary">
            Showing {{ filteredDefinitionPrompts.length }} of {{ definitionPrompts.length }} prompts
          </div>
        </div>
      </div>
    </div>

    <!-- STEP 4: OPTIMIZE & ENHANCE (Collapsible) -->
    <div class="workflow-section step-4-section" :class="{ 'collapsed': !showOptimizeSection }">
      <div class="step-header clickable" @click="showOptimizeSection = !showOptimizeSection">
        <div class="step-number">4</div>
        <div class="step-info">
          <h3>Optimize & Enhance Prompts</h3>
          <p>Use AI to analyze and improve your prompts for better accuracy.</p>
        </div>
        <div class="expand-indicator">
          <i :class="showOptimizeSection ? 'fas fa-chevron-up' : 'fas fa-chevron-down'"></i>
        </div>
      </div>

      <div v-if="showOptimizeSection" class="optimize-content">
        <div class="optimize-actions">
          <button 
            @click="runDryRunAnalysis" 
            :disabled="loading || !selectedCheckDefinition"
            class="btn btn-primary"
          >
            {{ loading ? 'Analyzing...' : '📊 Analyze Prompts' }}
          </button>
          
          <button 
            @click="analyzeFeedbackPatterns" 
            :disabled="loading"
            class="btn btn-warning"
          >
            {{ loading ? 'Analyzing...' : '🎯 Analyze Feedback' }}
          </button>
          
          <button 
            @click="runAutoOptimization" 
            :disabled="loading"
            class="btn btn-success"
          >
            {{ loading ? 'Optimizing...' : '🚀 Auto-Optimize' }}
          </button>
        </div>

        <!-- Analysis Results -->
        <div v-if="analysisResults" class="analysis-results-compact">
          <div class="stats-row">
            <div class="stat-item">
              <span class="stat-value">{{ analysisResults.total_requirements }}</span>
              <span class="stat-label">Total</span>
            </div>
            <div class="stat-item needs">
              <span class="stat-value">{{ analysisResults.needs_enhancement }}</span>
              <span class="stat-label">Need Enhancement</span>
            </div>
            <div class="stat-item enhanced">
              <span class="stat-value">{{ analysisResults.already_enhanced }}</span>
              <span class="stat-label">Enhanced</span>
            </div>
          </div>

          <!-- Requirements Needing Enhancement -->
          <div v-if="analysisResults.requirements_needing_enhancement?.length" class="enhancement-list">
            <h5>⚠️ Prompts Needing Enhancement</h5>
            <div class="enhancement-items">
              <div 
                v-for="req in analysisResults.requirements_needing_enhancement.slice(0, 5)" 
                :key="req.id"
                class="enhancement-item"
              >
                <div class="item-info">
                  <strong>{{ req.code }}</strong> - {{ req.name }}
                  <span class="chars-badge">{{ req.current_length }} chars</span>
                </div>
                <div class="item-actions">
                  <button 
                    v-if="req.enhancement_status === 'NEEDS_ENHANCEMENT'"
                    @click="enhanceRequirement(req.id)" 
                    class="btn btn-xs btn-success"
                  >
                    ✨ Enhance
                  </button>
                  <button 
                    v-if="req.can_apply && req.ready_to_apply"
                    @click="applyEnhancement(req.ready_to_apply.enhancement_log_id, req.id)" 
                    class="btn btn-xs btn-primary"
                  >
                    🚀 Apply
                  </button>
                </div>
              </div>
              <div v-if="analysisResults.requirements_needing_enhancement.length > 5" class="more-items">
                ... and {{ analysisResults.requirements_needing_enhancement.length - 5 }} more
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Preview Modal -->
    <div v-if="previewData" class="modal-overlay" @click="closePreview">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h3>📋 Enhancement Preview: {{ previewData.requirement_code }}</h3>
          <button @click="closePreview" class="btn-close">×</button>
        </div>
        
        <div class="modal-body">
          <div class="improvement-stats">
            <div class="stat">
              <label>Improvement Ratio:</label>
              <span class="highlight">{{ previewData.improvement_ratio?.toFixed(1) }}x longer</span>
            </div>
            <div class="stat">
              <label>Needs Enhancement:</label>
              <span :class="previewData.needs_enhancement ? 'text-warning' : 'text-success'">
                {{ previewData.needs_enhancement ? 'Yes' : 'No' }}
              </span>
            </div>
          </div>

          <div class="prompt-comparison">
            <div class="current-prompt">
              <h4>Current Prompt</h4>
              <div class="prompt-text">
                {{ previewData.current_prompt || 'No current prompt' }}
              </div>
            </div>
            
            <div class="enhanced-prompt">
              <h4>Enhanced Prompt (Preview)</h4>
              <div class="prompt-text enhanced">
                {{ previewData.enhanced_prompt?.substring(0, 1000) }}
                <span v-if="previewData.enhanced_prompt?.length > 1000">...</span>
              </div>
            </div>
          </div>
        </div>
        
        <div class="modal-footer">
          <button @click="closePreview" class="btn btn-secondary">Cancel</button>
          <button @click="enhanceFromPreview" class="btn btn-success">
            ✨ Apply Enhancement
          </button>
        </div>
      </div>
    </div>

    
    <!-- Feedback Analysis Results -->
    <div v-if="feedbackAnalysisResults" class="feedback-analysis-section">
      <div class="section-header">
        <h3><i class="fas fa-chart-line"></i> Feedback Analysis Results</h3>
        <p class="section-description">
          Based on user corrections and validation feedback from A/B testing
        </p>
      </div>
      
      <div class="feedback-stats-grid">
        <div class="feedback-stat-card">
          <h4>{{ feedbackAnalysisResults.requirements_with_feedback || 0 }}</h4>
          <p>Requirements with Feedback</p>
        </div>
        <div class="feedback-stat-card needs-improvement">
          <h4>{{ feedbackAnalysisResults.requirements_needing_improvement || 0 }}</h4>
          <p>Need Optimization</p>
        </div>
        <div class="feedback-stat-card enhanced">
          <h4>{{ feedbackAnalysisResults.requirements_with_enhancements || 0 }}</h4>
          <p>Have Enhancements</p>
        </div>
      </div>
      
      <!-- Top Issues Across Requirements -->
      <div v-if="feedbackAnalysisResults.top_issues_across_requirements" class="top-issues-section">
        <h4><i class="fas fa-exclamation-triangle"></i> Most Common Issues</h4>
        <div class="issues-grid">
          <div v-for="(count, issue) in feedbackAnalysisResults.top_issues_across_requirements" 
               :key="issue" 
               class="issue-card">
            <div class="issue-header">
              <span class="issue-name">{{ formatIssueName(issue) }}</span>
              <span class="issue-count">{{ count }} times</span>
            </div>
            <div class="issue-description">{{ getIssueDescription(issue) }}</div>
          </div>
        </div>
      </div>
      
      <!-- Improvement Opportunities -->
      <div v-if="feedbackAnalysisResults.improvement_opportunities?.length" class="improvement-opportunities">
        <h4><i class="fas fa-lightbulb"></i> Improvement Opportunities</h4>
        <div class="opportunities-list">
          <div v-for="opportunity in feedbackAnalysisResults.improvement_opportunities" 
               :key="opportunity.requirement_id" 
               class="opportunity-card">
            <div class="opportunity-header">
              <h5>{{ opportunity.requirement_name }}</h5>
              <span class="feedback-count">{{ opportunity.feedback_count }} feedback entries</span>
            </div>
            <div class="opportunity-issues">
              <span v-for="issue in opportunity.issues" 
                    :key="issue" 
                    class="issue-tag">
                {{ issue }}
              </span>
            </div>
            <div class="opportunity-actions">
              <button @click="generateFeedbackEnhancement(opportunity.requirement_id)" 
                      class="btn btn-sm btn-primary"
                      :disabled="loading">
                <i class="fas fa-magic"></i> Generate Feedback-Driven Enhancement
              </button>
              <button @click="analyzeSingleRequirementFeedback(opportunity.requirement_id)" 
                      class="btn btn-sm btn-outline-info">
                <i class="fas fa-search"></i> View Feedback Details
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- GLOBAL PROMPT LIBRARY (Collapsible - for viewing all prompts across definitions) -->
    <div class="workflow-section library-section" :class="{ 'collapsed': !showGlobalLibrary }">
      <div class="step-header clickable" @click="showGlobalLibrary = !showGlobalLibrary">
        <div class="step-number">📚</div>
        <div class="step-info">
          <h3>Global Prompt Library</h3>
          <p>View and manage all prompts across all definitions.</p>
        </div>
        <div class="expand-indicator">
          <i :class="showGlobalLibrary ? 'fas fa-chevron-up' : 'fas fa-chevron-down'"></i>
        </div>
      </div>

      <div v-if="showGlobalLibrary" class="library-content">
        <div class="library-controls-row">
          <button 
            @click="loadPromptLibrary" 
            :disabled="loading"
            class="btn btn-primary"
          >
            {{ loading ? 'Loading...' : '🔄 Load All Prompts' }}
          </button>
          
          <button 
            @click="showTemplateLibrary = true" 
            class="btn btn-outline"
          >
            📋 Templates
          </button>
          
          <button 
            @click="exportPrompts" 
            :disabled="loading"
            class="btn btn-outline"
          >
            📤 Export All
          </button>
        </div>

      <!-- Prompt Library -->
      <div v-if="promptLibrary && promptLibrary.length > 0" class="prompt-library">
        <div class="library-header">
          <h3>Prompt Library ({{ filteredPrompts.length }} of {{ promptLibrary.length }} prompts{{ showInactivePrompts ? ' (including inactive)' : '' }})</h3>
          <div class="library-controls">
            <div class="library-filters">
              <select v-model="libraryFilter" class="form-control">
                <option value="">All Check Definitions</option>
                <option v-for="definition in availableCheckDefinitions" :key="definition.id" :value="definition.id">
                  {{ definition.name }} ({{ definition.project_type }})
                </option>
              </select>
              <input 
                v-model="librarySearch" 
                placeholder="Search prompts..." 
                class="form-control"
              >
              <div class="inactive-toggle">
                <label class="toggle-switch">
                  <input 
                    type="checkbox" 
                    v-model="showInactivePrompts"
                  >
                  <span class="toggle-slider"></span>
                </label>
                <span class="toggle-label">Show Inactive</span>
              </div>
            </div>
            <div class="view-toggle">
              <div class="toggle-buttons">
                <button 
                  @click="libraryViewMode = 'cards'"
                  :class="['btn', 'btn-sm', libraryViewMode === 'cards' ? 'btn-primary' : 'btn-outline']"
                >
                  <i class="fas fa-th-large"></i> Cards
                </button>
                <button 
                  @click="libraryViewMode = 'table'"
                  :class="['btn', 'btn-sm', libraryViewMode === 'table' ? 'btn-primary' : 'btn-outline']"
                >
                  <i class="fas fa-table"></i> Table
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- Card View -->
        <div v-if="libraryViewMode === 'cards'" class="library-grid">
          <div 
            v-for="prompt in filteredPrompts" 
            :key="prompt.id"
            class="prompt-card"
            :class="{ 
              'enhanced': prompt.has_enhanced_version,
              'inactive': !prompt.is_active 
            }"
          >
            <div class="prompt-header">
              <h4>{{ prompt.name }}</h4>
              <div class="prompt-badges">
                <span class="check-definition-badge">{{ prompt.check_definition_name }}</span>
                <span v-if="prompt.has_enhanced_version" class="enhancement-badge">Enhanced</span>
                <span class="severity-badge" :class="prompt.severity">{{ prompt.severity }}</span>
                <div class="active-toggle">
                  <label class="toggle-switch">
                    <input 
                      type="checkbox" 
                      :checked="prompt.is_active" 
                      @change="toggleRequirementActive(prompt)"
                      :disabled="prompt.toggling"
                    >
                    <span class="toggle-slider"></span>
                  </label>
                  <span class="toggle-label">{{ prompt.is_active ? 'Active' : 'Inactive' }}</span>
                </div>
              </div>
            </div>
            
            <p class="prompt-description">{{ prompt.description }}</p>
            
            <div class="prompt-stats">
              <span class="stat">{{ prompt.prompt_length }} chars</span>
              <span class="stat">{{ prompt.check_definition_name }}</span>
            </div>
            
            <div class="prompt-actions">
              <button 
                @click="editPrompt(prompt)" 
                class="btn btn-sm btn-primary"
              >
                ✏️ Edit
              </button>
              <button 
                @click="duplicatePrompt(prompt)" 
                class="btn btn-sm btn-outline"
              >
                📄 Duplicate
              </button>
              <button 
                @click="testPrompt(prompt)" 
                class="btn btn-sm btn-outline"
              >
                🧪 Test
              </button>
            </div>
          </div>
        </div>

        <!-- Table View -->
        <div v-if="libraryViewMode === 'table'" class="library-table">
          <div class="table-container">
            <table class="prompt-table">
              <thead>
                <tr>
                  <th class="sortable" @click="sortTable('name')">
                    Name
                    <i :class="getSortIcon('name')"></i>
                  </th>
                  <th class="sortable" @click="sortTable('check_definition_name')">
                    Check Definition
                    <i :class="getSortIcon('check_definition_name')"></i>
                  </th>
                  <th class="sortable" @click="sortTable('type')">
                    Type
                    <i :class="getSortIcon('type')"></i>
                  </th>
                  <th class="sortable" @click="sortTable('severity')">
                    Severity
                    <i :class="getSortIcon('severity')"></i>
                  </th>
                  <th>Description</th>
                  <th class="sortable" @click="sortTable('prompt_length')">
                    Length
                    <i :class="getSortIcon('prompt_length')"></i>
                  </th>
                  <th>Status</th>
                  <th>Active</th>
                  <th class="actions-column">Actions</th>
                </tr>
              </thead>
              <tbody>
                <template v-for="prompt in sortedFilteredPrompts" :key="prompt.id || `prompt-${Math.random()}`">
                  <tr 
                    v-if="prompt && prompt.id"
                    class="prompt-row"
                    :class="{ 
                      'enhanced': prompt.has_enhanced_version,
                      'inactive': !prompt.is_active 
                    }"
                  >
                  <td class="name-cell">
                    <div class="name-content">
                      <strong>{{ prompt.name }}</strong>
                      <small class="code-text">{{ prompt.code }}</small>
                    </div>
                  </td>
                  <td class="definition-cell">
                    <span class="check-definition-badge table-badge">
                      {{ prompt.check_definition_name }}
                    </span>
                  </td>
                  <td class="type-cell">
                    <span class="type-badge">{{ formatType(prompt.type) }}</span>
                  </td>
                  <td class="severity-cell">
                    <span class="severity-badge table-severity" :class="prompt.severity?.toLowerCase()">
                      {{ prompt.severity }}
                    </span>
                  </td>
                  <td class="description-cell">
                    <div class="description-content">
                      <span class="description-text" :title="prompt.description">
                        {{ truncateText(prompt.description, 100) }}
                      </span>
                      <div v-if="prompt.prompt_template" class="prompt-preview" :title="prompt.prompt_template">
                        <small>{{ truncateText(prompt.prompt_template, 150) }}</small>
                      </div>
                    </div>
                  </td>
                  <td class="length-cell">
                    <div class="length-info">
                      <span class="length-chars">{{ prompt.prompt_length }}</span>
                      <small class="length-label">chars</small>
                    </div>
                  </td>
                  <td class="status-cell">
                    <div class="status-badges">
                      <span v-if="prompt.has_enhanced_version" class="enhancement-badge table-enhanced">
                        ✨ Enhanced
                      </span>
                      <span v-else class="basic-badge">
                        📝 Basic
                      </span>
                    </div>
                  </td>
                  <td class="active-cell">
                    <div class="table-toggle">
                      <label class="toggle-switch compact">
                        <input 
                          type="checkbox" 
                          :checked="prompt.is_active" 
                          @change="toggleRequirementActive(prompt)"
                          :disabled="prompt.toggling"
                        >
                        <span class="toggle-slider"></span>
                      </label>
                    </div>
                  </td>
                  <td class="actions-cell">
                    <div class="table-actions">
                      <button 
                        @click="editPrompt(prompt)" 
                        class="btn btn-xs btn-primary"
                        title="Edit Prompt"
                      >
                        <i class="fas fa-edit"></i>
                      </button>
                      <button 
                        @click="duplicatePrompt(prompt)" 
                        class="btn btn-xs btn-outline"
                        title="Duplicate Prompt"
                      >
                        <i class="fas fa-copy"></i>
                      </button>
                      <button 
                        @click="testPrompt(prompt)" 
                        class="btn btn-xs btn-outline"
                        title="Test Prompt"
                      >
                        <i class="fas fa-flask"></i>
                      </button>
                      <button 
                        @click="expandPromptRow(prompt.id)" 
                        class="btn btn-xs btn-outline"
                        :title="(prompt.id && expandedRows.includes(prompt.id)) ? 'Collapse' : 'Expand Details'"
                        v-if="prompt && prompt.id"
                      >
                        <i :class="(prompt.id && expandedRows.includes(prompt.id)) ? 'fas fa-chevron-up' : 'fas fa-chevron-down'"></i>
                      </button>
                    </div>
                  </td>
                  </tr>
                  <!-- Expanded Row Details -->
                  <tr v-if="prompt && prompt.id && expandedRows.includes(prompt.id)" class="expanded-row" :key="`${prompt.id}-expanded`">
                  <td colspan="9" class="expanded-content">
                    <div class="expanded-details">
                      <div class="detail-section">
                        <h5>📋 Full Description</h5>
                        <p>{{ prompt.description || 'No description provided' }}</p>
                      </div>
                      
                      <div class="detail-section">
                        <h5>🔧 Prompt Template</h5>
                        <div class="template-preview-full">
                          <pre>{{ prompt.prompt_template || 'No template available' }}</pre>
                        </div>
                      </div>
                      
                      <div class="detail-section" v-if="prompt.expected_value || prompt.case_sensitive !== undefined">
                        <h5>⚙️ Validation Settings</h5>
                        <div class="validation-info">
                          <div v-if="prompt.expected_value" class="validation-item">
                            <strong>Expected Value:</strong> {{ prompt.expected_value }}
                          </div>
                          <div v-if="prompt.case_sensitive !== undefined" class="validation-item">
                            <strong>Case Sensitive:</strong> {{ prompt.case_sensitive ? 'Yes' : 'No' }}
                          </div>
                        </div>
                      </div>
                    </div>
                  </td>
                  </tr>
                </template>
              </tbody>
            </table>
          </div>
        </div>
      </div>
      </div><!-- End library-content -->
    </div><!-- End library-section -->

    <!-- Manual Prompt Editor Modal -->
    <div v-if="editingPrompt" class="modal-overlay">
      <div class="modal-content prompt-editor-modal" @click.stop>
        <div class="modal-header">
          <h3>
            {{ editingPrompt.id ? '✏️ Edit Prompt' : '➕ Create New Prompt' }}: {{ editingPrompt.name || 'New Prompt' }}
            <span v-if="hasUnsavedChanges" class="unsaved-indicator" title="You have unsaved changes">●</span>
          </h3>
          <button @click="closePromptEditor" class="btn-close">×</button>
        </div>
        
        <div class="modal-body">
          <div class="prompt-editor-form">
            <!-- Basic Info -->
            <div class="form-section">
              <h4>Basic Information</h4>
              <div class="form-row">
                <div class="form-group">
                  <label>Name *</label>
                  <input 
                    v-model="editingPrompt.name" 
                    class="form-control" 
                    placeholder="Descriptive prompt name"
                    required
                  >
                </div>
                <div class="form-group">
                  <label>Code *</label>
                  <input 
                    v-model="editingPrompt.code" 
                    class="form-control" 
                    placeholder="unique_code_name"
                    :disabled="!!editingPrompt.id"
                    required
                  >
                </div>
              </div>
              
              <div class="form-row">
                <div class="form-group">
                  <label>Comprehensive Check Definition *</label>
                  <select v-model="editingPrompt.check_definition_id" class="form-control" required>
                    <option value="">Select Check Definition</option>
                    <option v-for="definition in availableCheckDefinitions" :key="definition.id" :value="definition.id">
                      {{ definition.name }} ({{ definition.project_type }})
                    </option>
                  </select>
                  <small class="form-help">Choose which comprehensive check this prompt belongs to</small>
                </div>
              </div>

              <div class="form-row">
                <div class="form-group">
                  <label>Type *</label>
                  <select v-model="editingPrompt.type" class="form-control" required>
                    <option value="">Select Type</option>
                    <option value="text_match">Text Match</option>
                    <option value="coverage_amount">Coverage Amount</option>
                    <option value="date_check">Date Check</option>
                    <option value="entity_verification">Entity Verification</option>
                    <option value="checkbox_verification">Checkbox/Y-N Verification</option>
                    <option value="custom">Custom</option>
                  </select>
                </div>
                <div class="form-group">
                  <label>Severity</label>
                  <select v-model="editingPrompt.severity" class="form-control">
                    <option value="LOW">Low</option>
                    <option value="MEDIUM">Medium</option>
                    <option value="HIGH">High</option>
                    <option value="CRITICAL">Critical</option>
                  </select>
                </div>
              </div>
              
              <div class="form-group">
                <label>Description</label>
                <textarea 
                  v-model="editingPrompt.description" 
                  class="form-control" 
                  rows="2"
                  placeholder="What this prompt checks for..."
                ></textarea>
              </div>
            </div>

            <!-- Prompt Template -->
            <div class="form-section">
              <h4>Prompt Template</h4>
              <div class="template-tools">
                <button 
                  @click="applyTemplate('text_match')" 
                  class="btn btn-sm btn-outline"
                  type="button"
                >
                  Apply Text Match Template
                </button>
                <button 
                  @click="applyTemplate('coverage_amount')" 
                  class="btn btn-sm btn-outline"
                  type="button"
                >
                  Apply Coverage Template
                </button>
                <button 
                  @click="aiEnhanceCurrentPrompt" 
                  class="btn btn-sm btn-success"
                  type="button"
                  :disabled="loading"
                >
                  🤖 AI Enhance
                </button>
              </div>
              
              <div class="prompt-editor">
                <textarea 
                  v-model="editingPrompt.prompt_template" 
                  class="form-control prompt-textarea" 
                  rows="20"
                  placeholder="Enter your prompt template here..."
                ></textarea>
                <div class="prompt-stats-inline">
                  <span>{{ (editingPrompt.prompt_template || '').length }} characters</span>
                  <span>{{ Math.ceil((editingPrompt.prompt_template || '').length / 4) }} tokens (est.)</span>
                </div>
              </div>
            </div>

            <!-- Validation Logic -->
            <div class="form-section">
              <h4>Validation Logic</h4>
              <div class="form-row">
                <div class="form-group">
                  <label>Expected Pattern/Value</label>
                  <input 
                    v-model="editingPrompt.expected_value" 
                    class="form-control" 
                    placeholder="Expected text, amount, or pattern"
                  >
                </div>
                <div class="form-group">
                  <label>Case Sensitive</label>
                  <select v-model="editingPrompt.case_sensitive" class="form-control">
                    <option :value="false">No</option>
                    <option :value="true">Yes</option>
                  </select>
                </div>
              </div>
              
              <!-- Temporal Context Setting -->
              <div class="form-group checkbox-group">
                <label class="checkbox-label">
                  <input 
                    type="checkbox" 
                    v-model="editingPrompt.requires_temporal_context"
                  >
                  <span class="checkbox-text">
                    <strong>🕐 Include Current Date Context</strong>
                    <small>Provides today's date to the AI for time-sensitive checks (expiration dates, certificate validity)</small>
                  </span>
                </label>
              </div>
            </div>
          </div>
        </div>
        
        <div class="modal-footer">
          <button @click="closePromptEditor" class="btn btn-secondary">Cancel</button>
          <button 
            @click="savePrompt" 
            class="btn btn-success"
            :disabled="!canSavePrompt || loading"
          >
            {{ loading ? 'Saving...' : (editingPrompt.id ? 'Update Prompt' : 'Create Prompt') }}
          </button>
        </div>
      </div>
    </div>

    <!-- Copy Check Definition Modal -->
    <div v-if="showCopyCheckDefinitionModal" class="modal-overlay">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h3>📄 Copy Check Definition</h3>
          <button @click="closeCopyDefinitionModal" class="btn-close">×</button>
        </div>
        
        <div class="modal-body">
          <div v-if="fullCheckDefinitionData" class="copy-source-info">
            <h4>Copying from:</h4>
            <div class="source-definition-card">
              <strong>{{ fullCheckDefinitionData.name }}</strong>
              <p>{{ fullCheckDefinitionData.description }}</p>
              <div class="definition-stats">
                <span class="stat">{{ fullCheckDefinitionData.requirements?.length || 0 }} requirements</span>
                <span class="stat">{{ fullCheckDefinitionData.project_type }}</span>
              </div>
            </div>
          </div>
          
          <div class="copy-form">
            <div class="form-group">
              <label>New Definition Name *</label>
              <input 
                v-model="copyDefinitionForm.name" 
                class="form-control" 
                placeholder="Enter name for the copied definition"
                required
              >
            </div>
            
            <div class="form-group">
              <label>Description</label>
              <textarea 
                v-model="copyDefinitionForm.description" 
                class="form-control" 
                rows="3"
                placeholder="Optional description for the copied definition"
              ></textarea>
            </div>
            
            <div class="copy-preview">
              <h5>What will be copied:</h5>
              <ul>
                <li>✅ Check definition settings (project type, document types)</li>
                <li>✅ All {{ fullCheckDefinitionData?.requirements?.length || 0 }} requirements</li>
                <li>✅ Prompt templates and validation logic</li>
                <li>✅ Severity levels and configurations</li>
              </ul>
              <p class="note">
                <strong>Note:</strong> Requirement codes will have "_copy" appended to ensure uniqueness.
              </p>
            </div>
          </div>
        </div>
        
        <div class="modal-footer">
          <button @click="closeCopyDefinitionModal" class="btn btn-secondary">Cancel</button>
          <button 
            @click="copyCheckDefinition" 
            class="btn btn-success"
            :disabled="!copyDefinitionForm.name.trim() || loading"
          >
            {{ loading ? 'Copying...' : '📄 Copy Definition' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Create New Check Definition Modal -->
    <div v-if="showCreateDefinitionModal" class="modal-overlay">
      <div class="modal-content create-definition-modal" @click.stop>
        <div class="modal-header">
          <h3>➕ Create New Check Definition</h3>
          <button @click="closeCreateDefinitionModal" class="btn-close">×</button>
        </div>
        
        <div class="modal-body">
          <p class="modal-description">
            Create a new check definition to organize your compliance requirements. 
            You can add prompts to it after creation.
          </p>
          
          <div class="create-definition-form">
            <div class="form-group">
              <label>Definition Name *</label>
              <input 
                v-model="createDefinitionForm.name" 
                class="form-control" 
                placeholder="e.g., Pyramid Insurance Requirements"
                required
              >
            </div>
            
            <div class="form-group">
              <label>Description</label>
              <textarea 
                v-model="createDefinitionForm.description" 
                class="form-control" 
                rows="3"
                placeholder="Describe what this check definition is for..."
              ></textarea>
            </div>
            
            <div class="form-row">
              <div class="form-group">
                <label>Project Type *</label>
                <select v-model="createDefinitionForm.project_type" class="form-control">
                  <option value="CERT_REVIEW">Certificate Review (COI)</option>
                  <option value="COMPLIANCE">Compliance Review</option>
                  <option value="SCOPE_OF_WORK">Scope of Work / Contract</option>
                  <option value="INSURANCE_REVIEW">Insurance Review</option>
                  <option value="GENERAL">General</option>
                </select>
              </div>
              
              <div class="form-group">
                <label>Application Type</label>
                <select v-model="createDefinitionForm.check_application_type" class="form-control">
                  <option value="COI_COMPLIANCE">Certificate of Insurance Compliance</option>
                  <option value="HEALTHCARE_CREDENTIALING">Healthcare Provider Credentialing</option>
                  <option value="MEDICAID_ENROLLMENT">Medicaid Provider Enrollment</option>
                  <option value="POLICY_REVIEW">Policy/Procedure Document Review</option>
                  <option value="CONTRACT_REVIEW">Contract Document Review</option>
                  <option value="REGULATORY_COMPLIANCE">Regulatory Compliance Review</option>
                  <option value="GENERAL">General Document Analysis</option>
                </select>
              </div>
            </div>
            
            <div class="form-group">
              <label>Document Types (comma-separated)</label>
              <input 
                v-model="createDefinitionForm.document_types_text" 
                class="form-control" 
                placeholder="e.g., COI, CERTIFICATE_OF_INSURANCE, ACORD_25"
              >
              <small class="form-hint">Leave blank to apply to all document types</small>
            </div>
          </div>
        </div>
        
        <div class="modal-footer">
          <button @click="closeCreateDefinitionModal" class="btn btn-secondary">Cancel</button>
          <button 
            @click="createNewCheckDefinition" 
            class="btn btn-success"
            :disabled="!createDefinitionForm.name.trim() || loading"
          >
            {{ loading ? 'Creating...' : '➕ Create Definition' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Add New Prompt Modal -->
    <div v-if="showAddNewPrompt" class="modal-overlay" @click="closeAddNewPrompt">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h3>➕ Add New Prompt</h3>
          <button @click="closeAddNewPrompt" class="btn-close">×</button>
        </div>
        
        <div class="modal-body">
          <div class="new-prompt-options">
            <div class="option-card" @click="createPromptFromTemplate('blank')">
              <div class="option-icon">📝</div>
              <h4>Blank Prompt</h4>
              <p>Start with a completely blank prompt and build from scratch</p>
            </div>
            
            <div class="option-card" @click="createPromptFromTemplate('text_match')">
              <div class="option-icon">🔍</div>
              <h4>Text Match Template</h4>
              <p>Check if specific text appears exactly in the document</p>
            </div>
            
            <div class="option-card" @click="createPromptFromTemplate('coverage_amount')">
              <div class="option-icon">💰</div>
              <h4>Coverage Amount Template</h4>
              <p>Verify insurance coverage amounts meet requirements</p>
            </div>
            
            <div class="option-card" @click="createPromptFromTemplate('entity_verification')">
              <div class="option-icon">🏢</div>
              <h4>Entity Verification Template</h4>
              <p>Confirm entity names, addresses, and relationships</p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Bulk Prompt Creator Modal -->
    <div v-if="showBulkPromptCreator" class="modal-overlay bulk-modal-overlay">
      <div class="modal-content bulk-prompt-modal" @click.stop>
        <div class="modal-header">
          <div class="bulk-modal-title">
            <h3>📝 Bulk Prompt Creator</h3>
            <p class="bulk-modal-subtitle">Create multiple prompts efficiently - perfect for copying from documents</p>
          </div>
          <div class="bulk-modal-controls">
            <button @click="minimizeBulkModal" class="btn btn-sm btn-outline" title="Minimize (keeps your work)">
              <i class="fas fa-minus"></i>
            </button>
            <button @click="closeBulkPromptCreator" class="btn-close">×</button>
          </div>
        </div>
        
        <div class="modal-body bulk-modal-body">
          <!-- Progress Indicator -->
          <div class="bulk-progress" v-if="bulkPrompts.length > 0">
            <div class="progress-info">
              <span class="progress-text">{{ bulkPrompts.length }} prompts prepared</span>
              <span class="progress-actions">
                <button @click="clearAllBulkPrompts" class="btn btn-sm btn-outline">
                  🗑️ Clear All
                </button>
              </span>
            </div>
            <div class="progress-bar">
              <div class="progress-fill" :style="{width: bulkCreationProgress + '%'}"></div>
            </div>
          </div>

          <!-- Input Mode Toggle -->
          <div class="input-mode-section">
            <div class="mode-toggle-header">
              <h4>📋 Input Method</h4>
              <div class="mode-toggle-buttons">
                <button 
                  @click="bulkInputMode = 'text'" 
                  :class="['btn', 'btn-sm', bulkInputMode === 'text' ? 'btn-primary' : 'btn-outline']"
                >
                  📝 Text Input
                </button>
                <button 
                  @click="bulkInputMode = 'structured'" 
                  :class="['btn', 'btn-sm', bulkInputMode === 'structured' ? 'btn-primary' : 'btn-outline']"
                >
                  📊 Structured Input
                </button>
              </div>
            </div>
          </div>

          <!-- Text Input Section -->
          <div class="bulk-input-section" v-if="bulkInputMode === 'text'">
            <div class="bulk-input-header">
              <h4>📋 Paste Your Prompts</h4>
              <div class="input-format-toggle">
                <label class="toggle-label">Input Format:</label>
                <select v-model="bulkInputFormat" class="form-control form-control-sm">
                  <option value="line">One per line</option>
                  <option value="numbered">Numbered list</option>
                  <option value="bullet">Bullet points</option>
                  <option value="comma">Comma separated</option>
                </select>
              </div>
            </div>
            
            <div class="bulk-textarea-container">
              <textarea 
                v-model="bulkInputText" 
                class="form-control bulk-textarea"
                rows="12"
                placeholder="Paste your prompts here...

Examples:
• Check if certificate shows workers comp exclusion
• Verify additional insured endorsement is present  
• Confirm policy limits meet minimum requirements

Or:
1. Check workers comp exclusion
2. Verify additional insured
3. Confirm policy limits

Tip: This modal won't close if you click outside - your work is safe!"
                @input="parseBulkInput"
                @paste="handleBulkPaste"
              ></textarea>
              
              <div class="bulk-textarea-tools">
                <div class="textarea-stats">
                  <span>{{ bulkInputText.length }} characters</span>
                  <span>{{ parsedPrompts.length }} prompts detected</span>
                </div>
                <div class="textarea-actions">
                  <button @click="clearBulkInput" class="btn btn-sm btn-outline">
                    Clear
                  </button>
                  <button @click="formatBulkInput" class="btn btn-sm btn-outline">
                    Auto-format
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- Structured Input Section -->
          <div class="structured-input-section" v-if="bulkInputMode === 'structured'">
            <div class="structured-input-header">
              <h4>📊 Structured Prompt Data</h4>
              <p class="help-text">Enter prompts with full details. Use Tab to move between fields, Enter to add new rows.</p>
            </div>
            
            <div class="structured-table-container">
              <table class="structured-input-table">
                <thead>
                  <tr>
                    <th>Name *</th>
                    <th>Prompt Template *</th>
                    <th>Type</th>
                    <th>Severity</th>
                    <th>Description</th>
                    <th>Expected Value</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(row, index) in structuredRows" :key="index" class="structured-row">
                    <td>
                      <input 
                        v-model="row.name" 
                        class="form-control form-control-sm"
                        placeholder="Prompt name"
                        @keydown.tab="handleTabNavigation($event, index, 0)"
                        @keydown.enter="addNewStructuredRow"
                      >
                    </td>
                    <td>
                      <textarea 
                        v-model="row.prompt_template" 
                        class="form-control form-control-sm"
                        rows="2"
                        placeholder="Prompt template"
                        @keydown.tab="handleTabNavigation($event, index, 1)"
                        @keydown.enter="addNewStructuredRow"
                      ></textarea>
                    </td>
                    <td>
                      <select 
                        v-model="row.type" 
                        class="form-control form-control-sm"
                        @keydown.tab="handleTabNavigation($event, index, 2)"
                      >
                        <option value="text_match">Text Match</option>
                        <option value="coverage_amount">Coverage Amount</option>
                        <option value="checkbox_verification">Checkbox/Y-N</option>
                        <option value="entity_verification">Entity Verification</option>
                      </select>
                    </td>
                    <td>
                      <select 
                        v-model="row.severity" 
                        class="form-control form-control-sm"
                        @keydown.tab="handleTabNavigation($event, index, 3)"
                      >
                        <option value="LOW">Low</option>
                        <option value="MEDIUM">Medium</option>
                        <option value="HIGH">High</option>
                        <option value="CRITICAL">Critical</option>
                      </select>
                    </td>
                    <td>
                      <input 
                        v-model="row.description" 
                        class="form-control form-control-sm"
                        placeholder="Description"
                        @keydown.tab="handleTabNavigation($event, index, 4)"
                        @keydown.enter="addNewStructuredRow"
                      >
                    </td>
                    <td>
                      <input 
                        v-model="row.expected_value" 
                        class="form-control form-control-sm"
                        placeholder="Expected value"
                        @keydown.tab="handleTabNavigation($event, index, 5)"
                        @keydown.enter="addNewStructuredRow"
                      >
                    </td>
                    <td>
                      <button 
                        @click="removeStructuredRow(index)" 
                        class="btn btn-sm btn-outline"
                        :disabled="structuredRows.length === 1"
                      >
                        🗑️
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            
            <div class="structured-actions">
              <button @click="addNewStructuredRow" class="btn btn-sm btn-outline">
                ➕ Add Row
              </button>
              <button @click="clearStructuredRows" class="btn btn-sm btn-outline">
                🗑️ Clear All
              </button>
              <button @click="addStructuredToQueue" class="btn btn-sm btn-success" :disabled="!hasValidStructuredRows">
                ➡️ Add to Queue ({{ validStructuredRowsCount }})
              </button>
            </div>
          </div>

          <!-- Parsed Prompts Preview -->
          <div class="parsed-prompts-section" v-if="parsedPrompts.length > 0">
            <div class="section-header">
              <h4>🔍 Detected Prompts ({{ parsedPrompts.length }})</h4>
              <div class="section-actions">
                <button @click="addAllParsedPrompts" class="btn btn-sm btn-success">
                  ➕ Add All to Queue
                </button>
              </div>
            </div>
            
            <div class="parsed-prompts-list">
              <div 
                v-for="(prompt, index) in parsedPrompts" 
                :key="index" 
                class="parsed-prompt-item"
              >
                <div class="prompt-preview">
                  <span class="prompt-number">{{ index + 1 }}.</span>
                  <span class="prompt-text">{{ prompt.trim() }}</span>
                </div>
                <button 
                  @click="addSingleParsedPrompt(index)" 
                  class="btn btn-sm btn-outline"
                  :disabled="prompt.trim().length === 0"
                >
                  ➕
                </button>
              </div>
            </div>
          </div>

          <!-- Bulk Prompts Queue -->
          <div class="bulk-queue-section" v-if="bulkPrompts.length > 0">
            <div class="section-header">
              <h4>📋 Prompts Queue ({{ bulkPrompts.length }})</h4>
              <div class="section-actions">
                <button @click="processBulkPrompts" class="btn btn-success" :disabled="loading || bulkPrompts.length === 0">
                  <i class="fas fa-rocket"></i> Create All Prompts
                </button>
              </div>
            </div>
            
            <!-- Global Settings -->
            <div class="bulk-global-settings">
              <div class="settings-row">
                <div class="form-group">
                  <label>Default Check Definition</label>
                  <select v-model="bulkGlobalSettings.check_definition_id" class="form-control">
                    <option value="">Select Check Definition</option>
                    <option v-for="def in availableCheckDefinitions" :key="def.id" :value="def.id">
                      {{ def.name }}
                    </option>
                  </select>
                </div>
                <div class="form-group">
                  <label>Default Type</label>
                  <select v-model="bulkGlobalSettings.type" class="form-control">
                    <option value="text_match">Text Match</option>
                    <option value="coverage_amount">Coverage Amount</option>
                    <option value="checkbox_verification">Checkbox/Y-N</option>
                    <option value="entity_verification">Entity Verification</option>
                  </select>
                </div>
                <div class="form-group">
                  <label>Default Severity</label>
                  <select v-model="bulkGlobalSettings.severity" class="form-control">
                    <option value="LOW">Low</option>
                    <option value="MEDIUM">Medium</option>
                    <option value="HIGH">High</option>
                    <option value="CRITICAL">Critical</option>
                  </select>
                </div>
              </div>
            </div>
            
            <!-- Prompts Queue List -->
            <div class="bulk-prompts-list">
              <div 
                v-for="(prompt, index) in bulkPrompts" 
                :key="index" 
                class="bulk-prompt-item"
                :class="{ 'processing': prompt.processing, 'completed': prompt.completed, 'error': prompt.error }"
              >
                <div class="prompt-item-header">
                  <div class="prompt-item-title">
                    <span class="prompt-number">{{ index + 1 }}.</span>
                    <input 
                      v-model="prompt.name" 
                      class="form-control prompt-name-input"
                      placeholder="Prompt name (auto-generated if empty)"
                    >
                  </div>
                  <div class="prompt-item-actions">
                    <button @click="editBulkPrompt(index)" class="btn btn-sm btn-outline">
                      ✏️
                    </button>
                    <button @click="removeBulkPrompt(index)" class="btn btn-sm btn-outline">
                      🗑️
                    </button>
                  </div>
                </div>
                
                <div class="prompt-item-body" :class="{ 'expanded': prompt.expanded }">
                  <div class="prompt-preview-text">{{ prompt.prompt_template }}</div>
                  
                  <div class="prompt-item-details" v-if="prompt.expanded">
                    <div class="form-row">
                      <div class="form-group">
                        <label>Type</label>
                        <select v-model="prompt.type" class="form-control form-control-sm">
                          <option value="text_match">Text Match</option>
                          <option value="coverage_amount">Coverage Amount</option>
                          <option value="checkbox_verification">Checkbox/Y-N</option>
                          <option value="entity_verification">Entity Verification</option>
                        </select>
                      </div>
                      <div class="form-group">
                        <label>Severity</label>
                        <select v-model="prompt.severity" class="form-control form-control-sm">
                          <option value="LOW">Low</option>
                          <option value="MEDIUM">Medium</option>
                          <option value="HIGH">High</option>
                          <option value="CRITICAL">Critical</option>
                        </select>
                      </div>
                      <div class="form-group">
                        <label>Check Definition</label>
                        <select v-model="prompt.check_definition_id" class="form-control form-control-sm">
                          <option value="">Select Definition</option>
                          <option v-for="def in availableCheckDefinitions" :key="def.id" :value="def.id">
                            {{ def.name }}
                          </option>
                        </select>
                      </div>
                    </div>
                    
                    <div class="form-group">
                      <label>Prompt Template</label>
                      <textarea 
                        v-model="prompt.prompt_template" 
                        class="form-control"
                        rows="3"
                      ></textarea>
                    </div>
                  </div>
                  
                  <div class="prompt-item-status" v-if="prompt.processing || prompt.completed || prompt.error">
                    <div v-if="prompt.processing" class="status-processing">
                      <i class="fas fa-spinner fa-spin"></i> Creating...
                    </div>
                    <div v-if="prompt.completed" class="status-completed">
                      <i class="fas fa-check-circle"></i> Created successfully!
                    </div>
                    <div v-if="prompt.error" class="status-error">
                      <i class="fas fa-exclamation-circle"></i> Error: {{ prompt.error }}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <div class="modal-footer bulk-modal-footer">
          <div class="footer-left">
            <span class="help-text">💡 Tip: This modal stays open while you work - no accidental closes!</span>
          </div>
          <div class="footer-right">
            <button @click="closeBulkPromptCreator" class="btn btn-secondary">
              Close
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- CSV Import Modal -->
    <div v-if="showBulkImportModal" class="modal-overlay">
      <div class="modal-content csv-import-modal" @click.stop>
        <div class="modal-header">
          <div class="bulk-modal-title">
            <h3>📊 Import Prompts from CSV/Excel</h3>
            <p class="bulk-modal-subtitle">Import structured prompt data from spreadsheets - matches export format</p>
          </div>
          <div class="bulk-modal-controls">
            <button @click="closeBulkImportModal" class="btn-close">×</button>
          </div>
        </div>
        
        <div class="modal-body">
          <!-- Step 1: File Upload -->
          <div class="import-step" v-if="importStep === 1">
            <div class="step-header">
              <h4>📁 Step 1: Upload Your File</h4>
              <p class="help-text">Upload a CSV or Excel file containing your prompt data</p>
            </div>
            
            <div class="file-upload-section">
              <div class="upload-area" :class="{ 'dragover': isDragOver }" 
                   @dragover.prevent="isDragOver = true"
                   @dragleave.prevent="isDragOver = false"
                   @drop.prevent="handleFileDrop">
                <div class="upload-content">
                  <i class="fas fa-cloud-upload-alt upload-icon"></i>
                  <h5>Drop your file here or click to browse</h5>
                  <p>Supports CSV, Excel (.xlsx, .xls)</p>
                  <input 
                    type="file" 
                    ref="fileInput"
                    @change="handleFileSelect"
                    accept=".csv,.xlsx,.xls"
                    class="file-input-hidden"
                  >
                  <button @click="$refs.fileInput.click()" class="btn btn-primary">
                    Choose File
                  </button>
                </div>
              </div>
              
              <div v-if="selectedImportFile" class="file-info">
                <div class="file-details">
                  <i class="fas fa-file-alt"></i>
                  <div class="file-meta">
                    <strong>{{ selectedImportFile.name }}</strong>
                    <span>{{ formatFileSize(selectedImportFile.size) }}</span>
                  </div>
                  <button @click="clearSelectedFile" class="btn btn-sm btn-outline">
                    Remove
                  </button>
                </div>
              </div>
            </div>
            
            <!-- Expected Format Info -->
            <div class="format-info">
              <h5>📋 Expected Format</h5>
              <p>Your file should have these columns (matches export format):</p>
              <div class="expected-columns">
                <div class="column-grid">
                  <div class="column-item required">
                    <strong>Name</strong> - Prompt name
                  </div>
                  <div class="column-item required">
                    <strong>Code</strong> - Unique identifier
                  </div>
                  <div class="column-item required">
                    <strong>Type</strong> - text_match, coverage_amount, etc.
                  </div>
                  <div class="column-item required">
                    <strong>Severity</strong> - LOW, MEDIUM, HIGH, CRITICAL
                  </div>
                  <div class="column-item">
                    <strong>Description</strong> - Optional description
                  </div>
                  <div class="column-item">
                    <strong>Prompt Template</strong> - The actual prompt content
                  </div>
                  <div class="column-item required">
                    <strong>Check Definition</strong> - Check definition name
                  </div>
                  <div class="column-item">
                    <strong>Expected Value</strong> - For validation
                  </div>
                  <div class="column-item">
                    <strong>Case Sensitive</strong> - true/false
                  </div>
                </div>
                <div class="format-note">
                  <i class="fas fa-info-circle"></i>
                  <span>Required columns are marked. Other columns will be ignored.</span>
                </div>
              </div>
            </div>
            
            <div class="step-actions">
              <button @click="parseImportFile" :disabled="!selectedImportFile || parsing" class="btn btn-primary">
                {{ parsing ? 'Parsing...' : 'Parse File' }}
              </button>
            </div>
          </div>
          
          <!-- Step 2: Column Mapping -->
          <div class="import-step" v-if="importStep === 2">
            <div class="step-header">
              <h4>🔗 Step 2: Map Columns</h4>
              <p class="help-text">Map your file columns to prompt fields</p>
            </div>
            
            <div class="column-mapping">
              <div class="mapping-table">
                <table class="table">
                  <thead>
                    <tr>
                      <th>Prompt Field</th>
                      <th>Your Column</th>
                      <th>Sample Data</th>
                      <th>Required</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="field in importFields" :key="field.key">
                      <td>
                        <strong>{{ field.label }}</strong>
                        <small class="field-description">{{ field.description }}</small>
                      </td>
                      <td>
                        <select v-model="columnMapping[field.key]" class="form-control">
                          <option value="">-- Select Column --</option>
                          <option v-for="col in importColumns" :key="col" :value="col">
                            {{ col }}
                          </option>
                        </select>
                      </td>
                      <td>
                        <span class="sample-data" v-if="getSampleData(field.key)">
                          {{ getSampleData(field.key) }}
                        </span>
                        <span class="no-data" v-else>No data</span>
                      </td>
                      <td>
                        <span v-if="field.required" class="badge badge-danger">Required</span>
                        <span v-else class="badge badge-secondary">Optional</span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
            
            <div class="step-actions">
              <button @click="importStep = 1" class="btn btn-secondary">
                ← Back
              </button>
              <button @click="validateImportData" :disabled="!isValidMapping" class="btn btn-primary">
                Validate Data
              </button>
            </div>
          </div>
          
          <!-- Step 3: Preview & Import -->
          <div class="import-step" v-if="importStep === 3">
            <div class="step-header">
              <h4>👁️ Step 3: Preview & Import</h4>
              <p class="help-text">Review the data before importing</p>
            </div>
            
            <div class="import-preview">
              <div class="preview-stats">
                <div class="stat-item">
                  <strong>{{ validImportRows.length }}</strong>
                  <span>Valid rows</span>
                </div>
                <div class="stat-item error" v-if="invalidImportRows.length > 0">
                  <strong>{{ invalidImportRows.length }}</strong>
                  <span>Invalid rows</span>
                </div>
              </div>
              
              <!-- Error Summary -->
              <div v-if="invalidImportRows.length > 0" class="error-summary">
                <h5>⚠️ Validation Errors</h5>
                <div class="error-list">
                  <div v-for="(error, index) in importErrors.slice(0, 5)" :key="index" class="error-item">
                    <strong>Row {{ error.row }}:</strong> {{ error.message }}
                  </div>
                  <div v-if="importErrors.length > 5" class="more-errors">
                    ... and {{ importErrors.length - 5 }} more errors
                  </div>
                </div>
              </div>
              
              <!-- Preview Table -->
              <div class="preview-table">
                <h5>📋 Preview (first 10 rows)</h5>
                <table class="table table-sm">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Type</th>
                      <th>Severity</th>
                      <th>Check Definition</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(row, index) in validImportRows.slice(0, 10)" :key="index">
                      <td>{{ row.name }}</td>
                      <td>{{ row.type }}</td>
                      <td>{{ row.severity }}</td>
                      <td>{{ row.check_definition_name }}</td>
                      <td><span class="badge badge-success">Valid</span></td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
            
            <div class="step-actions">
              <button @click="importStep = 2" class="btn btn-secondary">
                ← Back
              </button>
              <button @click="executeImport" :disabled="validImportRows.length === 0 || importing" class="btn btn-success">
                {{ importing ? 'Importing...' : `Import ${validImportRows.length} Prompts` }}
              </button>
            </div>
          </div>
          
          <!-- Step 4: Results -->
          <div class="import-step" v-if="importStep === 4">
            <div class="step-header">
              <h4>✅ Import Complete</h4>
              <p class="help-text">Import results summary</p>
            </div>
            
            <div class="import-results">
              <div class="results-summary">
                <div class="result-stat success">
                  <i class="fas fa-check-circle"></i>
                  <div>
                    <strong>{{ importResults.success }}</strong>
                    <span>Successfully imported</span>
                  </div>
                </div>
                <div class="result-stat error" v-if="importResults.errors > 0">
                  <i class="fas fa-exclamation-circle"></i>
                  <div>
                    <strong>{{ importResults.errors }}</strong>
                    <span>Failed to import</span>
                  </div>
                </div>
              </div>
              
              <div v-if="importResults.errorDetails.length > 0" class="error-details">
                <h5>Error Details:</h5>
                <div class="error-list">
                  <div v-for="(error, index) in importResults.errorDetails" :key="index" class="error-item">
                    <strong>{{ error.name }}:</strong> {{ error.error }}
                  </div>
                </div>
              </div>
            </div>
            
            <div class="step-actions">
              <button @click="resetImportModal" class="btn btn-primary">
                Import More Files
              </button>
              <button @click="closeBulkImportModal" class="btn btn-success">
                Done
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Minimized Bulk Modal -->
    <div v-if="bulkModalMinimized" class="minimized-bulk-modal" @click="restoreBulkModal">
      <div class="minimized-content">
        <i class="fas fa-edit"></i>
        <span>Bulk Creator ({{ bulkPrompts.length }} prompts)</span>
      </div>
    </div>

    <!-- Template Library Modal -->
    <div v-if="showTemplateLibrary" class="modal-overlay" @click="closeTemplateLibrary">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h3>📋 Prompt Templates</h3>
          <button @click="closeTemplateLibrary" class="btn-close">×</button>
        </div>
        
        <div class="modal-body">
          <div class="template-library">
            <div v-for="template in promptTemplates" :key="template.id" class="template-card">
              <h4>{{ template.name }}</h4>
              <p>{{ template.description }}</p>
              <div class="template-preview">
                <pre>{{ template.template.substring(0, 200) }}...</pre>
              </div>
              <button 
                @click="useTemplate(template)" 
                class="btn btn-primary btn-sm"
              >
                Use This Template
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- NEW: Prompt Effectiveness Tracking Section -->
    <div class="effectiveness-section">
      <div class="section-header">
        <h2>📊 Prompt Effectiveness & A/B Testing</h2>
        <p class="section-description">
          Track the performance of your prompts with real-world usage data and human feedback validation.
        </p>
      </div>

      <div class="effectiveness-controls">
        <button 
          @click="loadEffectivenessData" 
          :disabled="loading"
          class="btn btn-primary"
        >
          {{ loading ? 'Loading...' : '📈 Load Effectiveness Data' }}
        </button>
        
        <select 
          v-model="effectivenessPeriod" 
          class="form-control"
          @change="loadEffectivenessData"
        >
          <option value="7">Last 7 days</option>
          <option value="30">Last 30 days</option>
          <option value="90">Last 90 days</option>
        </select>
      </div>

      <!-- Effectiveness Results -->
      <div v-if="effectivenessData" class="effectiveness-results">
        <div class="effectiveness-summary">
          <h3>A/B Testing Summary</h3>
          <p>Analysis period: {{ effectivenessData.period?.start_date }} to {{ effectivenessData.period?.end_date }}</p>
        </div>

        <!-- Recommendations -->
        <div v-if="effectivenessData.recommendations?.length > 0" class="recommendations-section">
          <h4>🎯 Recommendations</h4>
          <div v-for="rec in effectivenessData.recommendations" :key="rec.requirement" class="recommendation-card">
            <div class="rec-header">
              <span class="priority-badge" :class="rec.priority">{{ rec.priority.toUpperCase() }}</span>
              <strong>{{ rec.requirement }}</strong>
            </div>
            <p class="rec-reason">{{ rec.reason }}</p>
            <p class="rec-action">💡 {{ rec.recommendation }}</p>
          </div>
        </div>

        <!-- Detailed Metrics -->
        <div class="metrics-grid">
          <div v-for="(data, reqCode) in effectivenessData.summary" :key="reqCode" class="metric-card">
            <div class="metric-header">
              <h4>{{ data.requirement_name }}</h4>
              <span class="requirement-code">{{ reqCode }}</span>
            </div>
            
            <div class="comparison-grid">
              <div class="version-metrics original">
                <h5>📄 Original Prompt</h5>
                <div class="metric-item">
                  <span class="metric-label">Runs:</span>
                  <span class="metric-value">{{ data.original_metrics.total_runs }}</span>
                </div>
                <div class="metric-item">
                  <span class="metric-label">Pass Rate:</span>
                  <span class="metric-value">{{ (data.original_metrics.pass_rate * 100).toFixed(1) }}%</span>
                </div>
                <div class="metric-item">
                  <span class="metric-label">Confidence:</span>
                  <span class="metric-value">{{ (data.original_metrics.avg_confidence * 100).toFixed(1) }}%</span>
                </div>
                <div class="metric-item">
                  <span class="metric-label">Human Accuracy:</span>
                  <span class="metric-value">{{ (data.original_metrics.human_accuracy_rate * 100).toFixed(1) }}%</span>
                </div>
              </div>
              
              <div class="version-metrics enhanced">
                <h5>✨ Enhanced Prompt</h5>
                <div class="metric-item">
                  <span class="metric-label">Runs:</span>
                  <span class="metric-value">{{ data.enhanced_metrics.total_runs }}</span>
                </div>
                <div class="metric-item">
                  <span class="metric-label">Pass Rate:</span>
                  <span class="metric-value">{{ (data.enhanced_metrics.pass_rate * 100).toFixed(1) }}%</span>
                </div>
                <div class="metric-item">
                  <span class="metric-label">Confidence:</span>
                  <span class="metric-value">{{ (data.enhanced_metrics.avg_confidence * 100).toFixed(1) }}%</span>
                </div>
                <div class="metric-item">
                  <span class="metric-label">Human Accuracy:</span>
                  <span class="metric-value">{{ (data.enhanced_metrics.human_accuracy_rate * 100).toFixed(1) }}%</span>
                </div>
              </div>
            </div>
            
            <div class="improvement-indicators">
              <div class="improvement-item" :class="{ positive: data.improvement.pass_rate_change > 0, negative: data.improvement.pass_rate_change < 0 }">
                <span class="improvement-label">Pass Rate Change:</span>
                <span class="improvement-value">{{ data.improvement.pass_rate_change > 0 ? '+' : '' }}{{ (data.improvement.pass_rate_change * 100).toFixed(1) }}%</span>
              </div>
              <div class="improvement-item" :class="{ positive: data.improvement.confidence_change > 0, negative: data.improvement.confidence_change < 0 }">
                <span class="improvement-label">Confidence Change:</span>
                <span class="improvement-value">{{ data.improvement.confidence_change > 0 ? '+' : '' }}{{ (data.improvement.confidence_change * 100).toFixed(1) }}%</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Results/Messages -->
    <div v-if="message" class="message" :class="messageType">
      {{ message }}
    </div>
  </div>
</template>

<script>
import api from '@/services/api'

export default {
  name: 'PromptEnhancementManager',
  data() {
    return {
      loading: false,
      analysisResults: null,
      feedbackAnalysisResults: null,
      previewData: null,
      selectedCheckDefinition: '',
      message: '',
      messageType: 'info',
      promptLibrary: [],
      libraryFilter: '',
      librarySearch: '',
      libraryViewMode: 'cards', // 'cards' or 'table'
      showInactivePrompts: false, // Toggle to show/hide inactive prompts
      tableSortField: 'name',
      tableSortDirection: 'asc',
      expandedRows: [],
      availableCheckDefinitions: [],
      showAddNewPrompt: false,
      showTemplateLibrary: false,
      editingPrompt: null,
      
      // Bulk prompt creation state
      showBulkPromptCreator: false,
      bulkModalMinimized: false,
      bulkInputText: '',
      bulkInputFormat: 'line',
      bulkInputMode: 'text', // 'text' or 'structured'
      parsedPrompts: [],
      bulkPrompts: [],
      bulkCreationProgress: 0,
      bulkGlobalSettings: {
        check_definition_id: '',
        type: 'text_match',
        severity: 'MEDIUM'
      },
      
      // Structured input state
      structuredRows: [
        {
          name: '',
          prompt_template: '',
          type: 'text_match',
          severity: 'MEDIUM',
          description: '',
          expected_value: '',
          check_definition_id: ''
        }
      ],
      
      // Track original prompt for unsaved changes detection
      originalPrompt: null,
      
      // Copy check definition state
      showCopyCheckDefinitionModal: false,
      copyDefinitionForm: {
        name: '',
        description: ''
      },
      fullCheckDefinitionData: null, // Store full definition data with requirements
      
      // Create new check definition state
      showCreateDefinitionModal: false,
      createDefinitionForm: {
        name: '',
        description: '',
        project_type: 'CERT_REVIEW',
        check_application_type: 'COI_COMPLIANCE',
        document_types_text: ''
      },
      
      // CSV Import state
      showBulkImportModal: false,
      importStep: 1,
      selectedImportFile: null,
      isDragOver: false,
      parsing: false,
      importing: false,
      importColumns: [],
      importData: [],
      columnMapping: {},
      validImportRows: [],
      invalidImportRows: [],
      importErrors: [],
      importResults: {
        success: 0,
        errors: 0,
        errorDetails: []
      },
      importFields: [
        { key: 'name', label: 'Name', description: 'Prompt name', required: true },
        { key: 'code', label: 'Code', description: 'Unique identifier', required: true },
        { key: 'type', label: 'Type', description: 'Prompt type', required: true },
        { key: 'severity', label: 'Severity', description: 'Severity level', required: true },
        { key: 'description', label: 'Description', description: 'Prompt description', required: false },
        { key: 'prompt_template', label: 'Prompt Template', description: 'The actual prompt content', required: false },
        { key: 'check_definition_name', label: 'Check Definition', description: 'Check definition name', required: true },
        { key: 'expected_value', label: 'Expected Value', description: 'Expected validation value', required: false },
        { key: 'case_sensitive', label: 'Case Sensitive', description: 'Case sensitivity flag', required: false }
      ],
      effectivenessData: null,
      effectivenessPeriod: 30,
      
      // Expandable sections state (to prevent console errors)
      showAboutSection: false,
      showHowToGuide: false,
      showExampleSection: false,
      
      // NEW: Redesigned UX state
      showOptimizeSection: false,
      showGlobalLibrary: false,
      
      // Definition-specific prompts
      definitionPrompts: [],
      loadingDefinitionPrompts: false,
      definitionPromptsSearch: '',
      definitionPromptsTypeFilter: '',
      showInactivePromptsInDefinition: false,
      definitionPromptCount: null,
      
      // Quick create simple prompt state
      showQuickCreate: false,
      quickPrompt: {
        name: '',
        type: '',
        check_definition_id: '',
        severity: 'MEDIUM',
        prompt_template: ''
      },
      promptTemplates: [
        {
          id: 1,
          name: 'Text Match Template',
          description: 'Template for exact text matching requirements',
          template: `You are a meticulous compliance reviewer. Your task is to determine if specific text appears exactly in the document.

REQUIREMENT: Check if the document contains the exact text: "{{expected_value}}"

ANALYSIS INSTRUCTIONS:
1. Search the document thoroughly for the exact text
2. Consider case sensitivity based on requirements
3. Look for the text in all sections and contexts

RESPONSE FORMAT:
{
  "result": "PASS|FAIL|UNCLEAR",
  "confidence": 0.95,
  "evidence": "Quote the exact text found in the document",
  "reasoning": "Explain where and how the text was found or why it was not found"
}`
        },
        {
          id: 2,
          name: 'Coverage Amount Template',
          description: 'Template for verifying insurance coverage amounts',
          template: `You are an expert insurance compliance analyst. Verify that coverage amounts meet the specified requirements.

REQUIREMENT: Verify that the insurance coverage amount for "{{coverage_type}}" meets or exceeds "{{minimum_amount}}"

ANALYSIS INSTRUCTIONS:
1. Locate the coverage section in the document
2. Find the specific coverage type mentioned
3. Extract the coverage amount
4. Compare against the required minimum

RESPONSE FORMAT:
{
  "result": "PASS|FAIL|PARTIAL|UNCLEAR",
  "confidence": 0.95,
  "evidence": "Quote the coverage amount found",
  "reasoning": "Explain the comparison and decision"
}`
        },
        {
          id: 3,
          name: 'Checkbox/Y-N Verification Template',
          description: 'Template for verifying checkboxes, X marks, and Y/N responses on forms',
          template: `You are a meticulous document analyst specializing in form verification. Your task is to examine checkboxes, marked fields, and Y/N responses.

REQUIREMENT: Check if "{{field_description}}" is marked as "{{expected_value}}" (X, Y, N, or checked/unchecked)

ANALYSIS INSTRUCTIONS:
1. Carefully examine the document for the specified field or question
2. Look for checkboxes, X marks, Y/N indicators, or checked/unchecked boxes
3. Pay special attention to ACORD forms and similar standardized documents
4. Consider various marking styles: ✓, X, ☑, ☒, Y, N, Yes, No, or filled boxes
5. Note the exact location and context of the marking

RESPONSE FORMAT:
{
  "result": "PASS|FAIL|UNCLEAR",
  "confidence": 0.95,
  "evidence": "Describe the exact marking found (e.g., 'X in box 15a', 'Y marked for workers comp exclusion')",
  "reasoning": "Explain what marking was found and whether it matches the expected value"
}`
        }
      ]
    }
  },
  
  computed: {
    filteredPrompts() {
      return this.getFilteredPrompts()
    },
    
    sortedFilteredPrompts() {
      const filtered = this.getFilteredPrompts()
      
      // Add safety check to filter out undefined/null items
      const validFiltered = filtered.filter(item => item && item.id)
      
      return validFiltered.sort((a, b) => {
        // Safety checks for the objects
        if (!a || !b) return 0
        
        let aVal = a[this.tableSortField]
        let bVal = b[this.tableSortField]
        
        // Handle undefined values
        if (aVal === undefined || aVal === null) aVal = ''
        if (bVal === undefined || bVal === null) bVal = ''
        
        // Handle different data types
        if (typeof aVal === 'string') {
          aVal = aVal.toLowerCase()
          bVal = (bVal || '').toLowerCase()
        }
        
        if (typeof aVal === 'number') {
          return this.tableSortDirection === 'asc' ? aVal - bVal : bVal - aVal
        }
        
        if (aVal < bVal) return this.tableSortDirection === 'asc' ? -1 : 1
        if (aVal > bVal) return this.tableSortDirection === 'asc' ? 1 : -1
        return 0
      })
    },
    
    canSavePrompt() {
      return this.editingPrompt && 
             this.editingPrompt.name && 
             this.editingPrompt.code && 
             this.editingPrompt.type && 
             this.editingPrompt.prompt_template &&
             this.editingPrompt.check_definition_id
    },
    
    hasUnsavedChanges() {
      if (!this.editingPrompt || !this.originalPrompt) {
        return false
      }
      
      // Compare key fields that matter for changes
      const fieldsToCompare = [
        'name', 'code', 'description', 'prompt_template', 'type', 
        'severity', 'check_definition_id', 'expected_value', 'case_sensitive'
      ]
      
      return fieldsToCompare.some(field => {
        return this.editingPrompt[field] !== this.originalPrompt[field]
      })
    },
    
    selectedCheckDefinitionData() {
      if (!this.selectedCheckDefinition) return null
      return this.availableCheckDefinitions.find(def => def.id == this.selectedCheckDefinition)
    },
    
    canCreateQuickPrompt() {
      return this.quickPrompt.name.trim() &&
             this.quickPrompt.type &&
             this.quickPrompt.check_definition_id &&
             this.quickPrompt.prompt_template.trim()
    },
    
    hasValidStructuredRows() {
      return this.structuredRows.some(row => 
        row.name.trim() && 
        row.prompt_template.trim() && 
        row.type &&
        this.bulkGlobalSettings.check_definition_id
      )
    },
    
    validStructuredRowsCount() {
      return this.structuredRows.filter(row => 
        row.name.trim() && 
        row.prompt_template.trim() && 
        row.type &&
        this.bulkGlobalSettings.check_definition_id
      ).length
    },
    
    isValidMapping() {
      const requiredFields = this.importFields.filter(f => f.required).map(f => f.key)
      return requiredFields.every(field => this.columnMapping[field])
    },
    
    // NEW: Computed for quick create that uses selected definition
    canCreateQuickPromptForSelected() {
      return this.quickPrompt.name.trim() &&
             this.quickPrompt.type &&
             this.selectedCheckDefinition &&
             this.quickPrompt.prompt_template.trim()
    },
    
    // NEW: Filtered prompts for the selected definition
    filteredDefinitionPrompts() {
      let prompts = this.definitionPrompts
      
      // Filter by active status
      if (!this.showInactivePromptsInDefinition) {
        prompts = prompts.filter(p => p.is_active)
      }
      
      // Filter by search
      if (this.definitionPromptsSearch) {
        const search = this.definitionPromptsSearch.toLowerCase()
        prompts = prompts.filter(p => 
          p.name?.toLowerCase().includes(search) ||
          p.code?.toLowerCase().includes(search) ||
          p.description?.toLowerCase().includes(search)
        )
      }
      
      // Filter by type
      if (this.definitionPromptsTypeFilter) {
        prompts = prompts.filter(p => p.type === this.definitionPromptsTypeFilter)
      }
      
      return prompts
    }
  },
  
  async mounted() {
    // Load check definitions to populate the dropdown
    await this.loadCheckDefinitions()
    // Show a more informative welcome message
    this.showMessage('Welcome! Start by selecting or creating a Check Definition, then add prompts to it.', 'info')
  },
  
  methods: {
    async loadCheckDefinitions() {
      try {
        const response = await api.get('/prompts/check-definitions/')
        const checkDefs = response.data.check_definitions || []
        this.availableCheckDefinitions = checkDefs
        console.log(`Loaded ${this.availableCheckDefinitions.length} check definitions for prompt filtering`)
        console.log('Check definitions:', this.availableCheckDefinitions)
      } catch (error) {
        // If check definitions aren't available, just use empty array
        console.warn('Check definitions not available for prompt filtering:', error.response?.status)
        this.availableCheckDefinitions = []
      }
    },

    async runDryRunAnalysis() {
      if (this.loading) {
        console.log('Analysis already running, skipping...')
        return
      }
      
      this.loading = true
      console.log('🔍 Starting dry run analysis for check definition:', this.selectedCheckDefinition)
      
      try {
        const payload = { action: 'dry_run' }
        if (this.selectedCheckDefinition) {
          payload.check_definition_id = parseInt(this.selectedCheckDefinition)
        }
        
        console.log('📡 Sending dry run request:', payload)
        const response = await api.post('/enhance-prompts/', payload)
        console.log('✅ Dry run response:', response.data)
        
        this.analysisResults = response.data
        this.showMessage(`Analysis complete: ${response.data.needs_enhancement} requirements need enhancement`, 'success')
      } catch (error) {
        console.error('❌ Analysis failed:', error)
        console.error('Error details:', error.response?.data)
        
        if (error.response?.status === 500) {
          this.showMessage('Server error during analysis. Please check the backend logs.', 'error')
        } else {
          this.showMessage('Analysis failed: ' + (error.response?.data?.error || error.message), 'error')
        }
      } finally {
        this.loading = false
      }
    },

    async previewEnhancement(requirementId) {
      this.loading = true
      try {
        const response = await api.post('/enhance-prompts/', {
          action: 'preview',
          requirement_id: requirementId
        })
        this.previewData = response.data
      } catch (error) {
        console.error('Preview failed:', error)
        this.showMessage('Preview failed: ' + (error.response?.data?.error || error.message), 'error')
      } finally {
        this.loading = false
      }
    },

    async enhanceRequirement(requirementId) {
      this.loading = true
      try {
        const response = await api.post('/enhance-prompts/', {
          action: 'enhance',
          requirement_id: requirementId
        })
        
        if (response.data.success) {
          // Show that enhancement was created but needs to be applied
          this.showMessage(
            `✨ Enhancement created for requirement ${requirementId}! Click "Apply to Production" to activate it.`, 
            'success'
          )
          // Refresh analysis to show new status
          await this.runDryRunAnalysis()
        } else {
          this.showMessage(response.data.message || 'Enhancement failed', 'error')
        }
      } catch (error) {
        console.error('Enhancement failed:', error)
        this.showMessage('Enhancement failed: ' + (error.response?.data?.error || error.message), 'error')
      } finally {
        this.loading = false
      }
    },

    async enhanceFromPreview() {
      if (this.previewData?.requirement_id) {
        await this.enhanceRequirement(this.previewData.requirement_id)
        this.closePreview()
      }
    },


    closePreview() {
      this.previewData = null
    },

    showMessage(text, type = 'info') {
      this.message = text
      this.messageType = type
      setTimeout(() => {
        this.message = ''
      }, 5000)
    },

    async onCheckDefinitionChange() {
      // Load prompts for the selected definition
      if (this.selectedCheckDefinition) {
        await this.loadPromptsForSelectedDefinition()
        // Reset quick prompt form
        this.quickPrompt = {
          name: '',
          type: '',
          check_definition_id: this.selectedCheckDefinition,
          severity: 'MEDIUM',
          prompt_template: ''
        }
      } else {
        // Clear results when "All Definitions" is selected
        this.analysisResults = null
        this.definitionPrompts = []
        this.definitionPromptCount = null
      }
    },

    // NEW: Load prompts specifically for the selected definition
    async loadPromptsForSelectedDefinition() {
      if (!this.selectedCheckDefinition) return
      
      this.loadingDefinitionPrompts = true
      try {
        const response = await api.get('/prompts/', {
          params: { check_definition_id: this.selectedCheckDefinition }
        })
        this.definitionPrompts = response.data || []
        this.definitionPromptCount = this.definitionPrompts.length
        console.log(`Loaded ${this.definitionPrompts.length} prompts for definition ${this.selectedCheckDefinition}`)
      } catch (error) {
        console.error('Failed to load prompts for definition:', error)
        this.showMessage('Failed to load prompts: ' + (error.response?.data?.error || error.message), 'error')
        this.definitionPrompts = []
        this.definitionPromptCount = 0
      } finally {
        this.loadingDefinitionPrompts = false
      }
    },
    
    // NEW: Create prompt using the selected definition (no need to pick again)
    async createSimplePromptForSelectedDefinition() {
      if (!this.canCreateQuickPromptForSelected) return
      
      this.loading = true
      try {
        // Generate a code from the name (same logic as existing createSimplePrompt)
        const code = this.quickPrompt.name
          .toLowerCase()
          .replace(/[^a-z0-9\s]/g, '')
          .replace(/\s+/g, '_')
          .substring(0, 50)
        
        const promptData = {
          name: this.quickPrompt.name.trim(),
          code: code,
          type: this.quickPrompt.type,
          prompt_template: this.quickPrompt.prompt_template.trim(),
          check_definition_id: parseInt(this.selectedCheckDefinition),
          severity: this.quickPrompt.severity,
          description: `Simple prompt: ${this.quickPrompt.prompt_template.substring(0, 100)}${this.quickPrompt.prompt_template.length > 100 ? '...' : ''}`
        }
        
        console.log('Creating prompt with data:', promptData)
        
        // Use the correct endpoint: /prompts/create/
        const response = await api.post('/prompts/create/', promptData)
        
        if (response.data) {
          const createdName = this.quickPrompt.name
          // Reset form but keep the selected definition - do this BEFORE showing message
          // so the form is immediately ready for new input
          this.quickPrompt = {
            name: '',
            type: '',
            check_definition_id: this.selectedCheckDefinition,
            severity: 'MEDIUM',
            prompt_template: ''
          }
          // Set loading to false BEFORE the reload so buttons become responsive
          this.loading = false
          this.showMessage(`✅ Created prompt "${createdName}" successfully! Form is ready for another prompt.`, 'success')
          // Reload prompts for this definition (uses its own loading state)
          await this.loadPromptsForSelectedDefinition()
          return // Exit early since we already set loading = false
        }
      } catch (error) {
        console.error('Failed to create prompt:', error)
        console.error('Error details:', {
          status: error.response?.status,
          statusText: error.response?.statusText,
          data: error.response?.data
        })
        
        let errorMessage = 'Failed to create prompt'
        if (error.response?.status === 405) {
          errorMessage = 'Method not allowed - please check if you are logged in'
        } else if (error.response?.data?.error) {
          errorMessage = `Failed to create prompt: ${error.response.data.error}`
        } else {
          errorMessage = `Failed to create prompt: ${error.message}`
        }
        
        this.showMessage(errorMessage, 'error')
      } finally {
        this.loading = false
      }
    },
    
    // NEW: Create and enhance prompt for selected definition
    async createAndEnhancePromptForSelectedDefinition() {
      if (!this.canCreateQuickPromptForSelected) return
      
      this.loading = true
      try {
        // First create the prompt (same logic as createSimplePromptForSelectedDefinition)
        const code = this.quickPrompt.name
          .toLowerCase()
          .replace(/[^a-z0-9\s]/g, '')
          .replace(/\s+/g, '_')
          .substring(0, 50)
        
        const promptData = {
          name: this.quickPrompt.name.trim(),
          code: code,
          type: this.quickPrompt.type,
          prompt_template: this.quickPrompt.prompt_template.trim(),
          check_definition_id: parseInt(this.selectedCheckDefinition),
          severity: this.quickPrompt.severity,
          description: `Simple prompt: ${this.quickPrompt.prompt_template.substring(0, 100)}${this.quickPrompt.prompt_template.length > 100 ? '...' : ''}`
        }
        
        console.log('Creating prompt with data:', promptData)
        
        // Use the correct endpoint: /prompts/create/
        const response = await api.post('/prompts/create/', promptData)
        
        if (response.data && response.data.id) {
          const createdName = this.quickPrompt.name
          const createdId = response.data.id
          
          // Reset form FIRST so it's immediately ready for new input
          this.quickPrompt = {
            name: '',
            type: '',
            check_definition_id: this.selectedCheckDefinition,
            severity: 'MEDIUM',
            prompt_template: ''
          }
          // Set loading to false so form is responsive while enhancement runs
          this.loading = false
          
          this.showMessage(`✅ Created prompt "${createdName}", now enhancing with AI...`, 'info')
          
          // Now enhance it (form is already reset and responsive)
          try {
            await api.post('/enhance-prompts/', {
              action: 'enhance',
              requirement_id: createdId
            })
            this.showMessage(`✨ Prompt "${createdName}" created and enhanced! Form is ready for another prompt.`, 'success')
          } catch (enhanceError) {
            console.warn('Enhancement failed, but prompt was created:', enhanceError)
            this.showMessage(`✅ Prompt created. Enhancement pending - you can enhance it later.`, 'warning')
          }
          
          // Reload prompts for this definition (uses its own loading state)
          await this.loadPromptsForSelectedDefinition()
          return // Exit early since we already set loading = false
        }
      } catch (error) {
        console.error('Failed to create and enhance prompt:', error)
        console.error('Error details:', {
          status: error.response?.status,
          statusText: error.response?.statusText,
          data: error.response?.data
        })
        
        let errorMessage = 'Failed to create prompt'
        if (error.response?.status === 405) {
          errorMessage = 'Method not allowed - please check if you are logged in'
        } else if (error.response?.data?.error) {
          errorMessage = `Failed to create prompt: ${error.response.data.error}`
        } else {
          errorMessage = `Failed to create prompt: ${error.message}`
        }
        
        this.showMessage(errorMessage, 'error')
      } finally {
        this.loading = false
      }
    },
    
    // NEW: Export prompts for the selected definition only
    async exportPromptsForDefinition() {
      if (!this.selectedCheckDefinition) return
      
      this.loading = true
      try {
        const response = await api.get('/prompts/export/', {
          params: { check_definition_id: this.selectedCheckDefinition },
          responseType: 'blob'
        })
        
        const defName = this.selectedCheckDefinitionData?.name || 'definition'
        const filename = `prompts_${defName.replace(/[^a-z0-9]/gi, '_')}_${new Date().toISOString().split('T')[0]}.csv`
        
        const url = window.URL.createObjectURL(new Blob([response.data]))
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', filename)
        document.body.appendChild(link)
        link.click()
        link.remove()
        window.URL.revokeObjectURL(url)
        
        this.showMessage(`📤 Exported ${this.definitionPrompts.length} prompts to ${filename}`, 'success')
      } catch (error) {
        console.error('Export failed:', error)
        this.showMessage('Export failed: ' + (error.response?.data?.error || error.message), 'error')
      } finally {
        this.loading = false
      }
    },
    
    // NEW: Format application type for display
    formatApplicationType(type) {
      const typeMap = {
        'COI_COMPLIANCE': 'Insurance/COI',
        'HEALTHCARE_CREDENTIALING': 'Healthcare',
        'MEDICAID_ENROLLMENT': 'Medicaid',
        'POLICY_REVIEW': 'Policy',
        'CONTRACT_REVIEW': 'Contract',
        'REGULATORY_COMPLIANCE': 'Regulatory',
        'GENERAL': 'General',
        // Legacy mappings for backwards compatibility
        'INSURANCE_COI': 'Insurance/COI',
        'POLICY_PROCEDURE': 'Policy',
        'CUSTOM': 'Custom'
      }
      return typeMap[type] || type
    },

    getSelectedDefinitionName() {
      if (!this.selectedCheckDefinition) return ''
      const def = this.availableCheckDefinitions.find(d => d.id == this.selectedCheckDefinition)
      return def ? def.name : 'Unknown Definition'
    },

    getSelectedDefinitionDescription() {
      if (!this.selectedCheckDefinition) return ''
      const def = this.availableCheckDefinitions.find(d => d.id == this.selectedCheckDefinition)
      return def ? (def.description || 'No description available') : 'Unknown Definition'
    },

    async applyEnhancement(enhancementLogId, requirementId) {
      this.loading = true
      try {
        const response = await api.post('/enhance-prompts/', {
          action: 'apply',
          enhancement_log_id: enhancementLogId
        })
        
        if (response.data.success) {
          this.showMessage(`🚀 Enhancement applied to production for requirement ${requirementId}!`, 'success')
          await this.runDryRunAnalysis()
        } else {
          this.showMessage(response.data.error || 'Failed to apply enhancement', 'error')
        }
      } catch (error) {
        console.error('Apply failed:', error)
        this.showMessage('Apply failed: ' + (error.response?.data?.error || error.message), 'error')
      } finally {
        this.loading = false
      }
    },

    async rollbackEnhancement(requirementId) {
      if (!confirm(`Rollback requirement ${requirementId} to original prompt?`)) return
      
      this.loading = true
      try {
        const response = await api.post('/enhance-prompts/', {
          action: 'rollback',
          requirement_id: requirementId
        })
        
        if (response.data.success) {
          this.showMessage(`↩️ Rolled back requirement ${requirementId} to original prompt`, 'success')
          await this.runDryRunAnalysis()
        } else {
          this.showMessage(response.data.error || 'Failed to rollback', 'error')
        }
      } catch (error) {
        console.error('Rollback failed:', error)
        this.showMessage('Rollback failed: ' + (error.response?.data?.error || error.message), 'error')
      } finally {
        this.loading = false
      }
    },

    async getRequirementStatus(requirementId) {
      try {
        const response = await api.post('/enhance-prompts/', {
          action: 'status',
          requirement_id: requirementId
        })
        return response.data
      } catch (error) {
        console.error('Status check failed:', error)
        return null
      }
    },

    getStatusText(status) {
      const statusMap = {
        'NEEDS_ENHANCEMENT': '⚡ Needs Enhancement',
        'READY_TO_APPLY': '🔄 Ready to Apply',
        'ENHANCED': '✅ Enhanced',
        'FAILED': '❌ Enhancement Failed',
        'PROCESSING': '⏳ Processing'
      }
      return statusMap[status] || status
    },

    async loadPromptLibrary() {
      this.loading = true
      try {
        const response = await api.get('/prompts/')
        this.promptLibrary = response.data
        this.showMessage(`Loaded ${this.promptLibrary.length} prompts from the library.`, 'info')
      } catch (error) {
        console.error('Failed to load prompt library:', error)
        this.showMessage('Failed to load prompt library: ' + (error.response?.data?.error || error.message), 'error')
      } finally {
        this.loading = false
      }
    },

    async toggleRequirementActive(prompt) {
      if (prompt.toggling) return
      
      prompt.toggling = true
      try {
        const response = await api.post(`/prompts/${prompt.id}/toggle-active/`)
        
        if (response.data.success) {
          prompt.is_active = response.data.is_active
          this.showMessage(response.data.message, 'success')
          
          // Reload the definition-specific prompts if a definition is selected
          if (this.selectedCheckDefinition) {
            await this.loadPromptsForSelectedDefinition()
          }
          
          // Also reload the global prompt library if it's loaded
          if (this.promptLibrary.length > 0) {
            await this.loadPromptLibrary()
          }
        } else {
          this.showMessage('Failed to toggle requirement status', 'error')
        }
      } catch (error) {
        console.error('Error toggling requirement active status:', error)
        this.showMessage('Error toggling requirement: ' + (error.response?.data?.error || error.message), 'error')
      } finally {
        prompt.toggling = false
      }
    },

    async editPrompt(prompt) {
      this.editingPrompt = { ...prompt }
      this.originalPrompt = { ...prompt } // Store original for change detection
      this.showMessage(`Editing prompt: ${prompt.name}`, 'info')
    },

    async savePrompt() {
      this.loading = true
      try {
        if (this.editingPrompt.id) {
          await api.put(`/prompts/${this.editingPrompt.id}/`, this.editingPrompt)
          this.showMessage(`Prompt "${this.editingPrompt.name}" updated.`, 'success')
        } else {
          await api.post('/prompts/create/', this.editingPrompt)
          this.showMessage(`Prompt "${this.editingPrompt.name}" created.`, 'success')
        }
        
        // Clear the editor without confirmation since we just saved
        this.editingPrompt = null
        this.originalPrompt = null
        this.loading = false
        
        // Refresh the definition-specific prompts list if a definition is selected
        if (this.selectedCheckDefinition) {
          await this.loadPromptsForSelectedDefinition()
        }
        
        // Also refresh global library if it's loaded
        if (this.promptLibrary.length > 0) {
          await this.loadPromptLibrary()
        }
        return
      } catch (error) {
        console.error('Save prompt failed:', error)
        this.showMessage('Save prompt failed: ' + (error.response?.data?.error || error.message), 'error')
      } finally {
        this.loading = false
      }
    },

    async duplicatePrompt(prompt) {
      const newPrompt = {
        name: `${prompt.name} (Copy)`,
        code: `${prompt.code}_copy_${Date.now().toString(36)}`,
        type: prompt.type,
        severity: prompt.severity,
        description: prompt.description,
        prompt_template: prompt.prompt_template,
        expected_value: prompt.expected_value,
        case_sensitive: prompt.case_sensitive,
        // Use the currently selected definition, or fall back to the prompt's original definition
        check_definition_id: this.selectedCheckDefinition || prompt.check_definition_id,
        check_definition_name: prompt.check_definition_name,
        prompt_length: prompt.prompt_length,
        has_enhanced_version: false,
        enhancement_log_id: null,
        enhancement_status: 'NEEDS_ENHANCEMENT',
        requires_temporal_context: prompt.requires_temporal_context !== false  // Preserve from original
      }
      this.editingPrompt = newPrompt
      this.showMessage(`Duplicated prompt: ${newPrompt.name}. It will be added to the currently selected definition.`, 'info')
    },

    async testPrompt(prompt) {
      this.loading = true
      try {
        const response = await api.post('/test-prompt/', { prompt: prompt.prompt_template })
        this.showMessage(`Prompt test successful! Response: ${response.data.message}`, 'success')
      } catch (error) {
        console.error('Prompt test failed:', error)
        this.showMessage('Prompt test failed: ' + (error.response?.data?.error || error.message), 'error')
      } finally {
        this.loading = false
      }
    },

    async createPromptFromTemplate(templateType) {
      // Pre-fill with the currently selected definition if one is selected
      const definitionId = this.selectedCheckDefinition || ''
      const definitionName = this.selectedCheckDefinitionData?.name || ''
      
      this.editingPrompt = {
        name: `${templateType.charAt(0).toUpperCase() + templateType.slice(1)} Template`,
        code: `template_${templateType}_${Date.now().toString(36)}`,
        type: templateType,
        severity: 'LOW',
        description: `A template for ${templateType} checks.`,
        prompt_template: this.getTemplateByType(templateType),
        expected_value: '',
        case_sensitive: false,
        check_definition_id: definitionId,
        check_definition_name: definitionName, 
        prompt_length: 0,
        has_enhanced_version: false,
        enhancement_log_id: null,
        enhancement_status: 'NEEDS_ENHANCEMENT',
        requires_temporal_context: templateType === 'date_check'  // Enable by default for date checks
      }
      
      if (definitionId) {
        this.showMessage(`Created new prompt from template: ${this.editingPrompt.name}. It will be added to "${definitionName}".`, 'info')
      } else {
        this.showMessage(`Created new prompt from template: ${this.editingPrompt.name}. Please select a check definition.`, 'info')
      }
      
      // Close the add new prompt modal
      this.showAddNewPrompt = false
    },

    getTemplateByType(type) {
      switch (type) {
        case 'text_match':
          return `Please check if the document contains the exact text: "{{ expected_value }}". Return "YES" or "NO".`
        case 'coverage_amount':
          return `Please verify that the insurance coverage amount for "{{ expected_value }}" is sufficient. Return "YES" or "NO".`
        case 'entity_verification':
          return `Please confirm that the entity name "{{ expected_value }}" is present in the document. Return "YES" or "NO".`
        case 'checkbox_verification':
          return `Please check if the field "{{ field_description }}" is marked as "{{ expected_value }}" (X, Y, N, or checked/unchecked). Look for checkboxes, X marks, or Y/N responses. Return "YES" if marked correctly, "NO" if not.`
        default:
          return 'Please check if the document contains the exact text: "{{ expected_value }}". Return "YES" or "NO".'
      }
    },

    async useTemplate(template) {
      this.editingPrompt = { ...template }
      this.showMessage(`Using template: ${template.name}`, 'info')
    },

    async aiEnhanceCurrentPrompt() {
      if (!this.editingPrompt || !this.editingPrompt.prompt_template) {
        this.showMessage('Please enter a prompt template to enhance.', 'warning')
        return
      }
      this.loading = true
      try {
        const response = await api.post('/ai-enhance-prompt/', { prompt: this.editingPrompt.prompt_template })
        this.editingPrompt.prompt_template = response.data.enhanced_prompt
        this.editingPrompt.prompt_length = response.data.enhanced_prompt.length
        this.showMessage(`Prompt enhanced by AI!`, 'success')
      } catch (error) {
        console.error('AI enhancement failed:', error)
        this.showMessage('AI enhancement failed: ' + (error.response?.data?.error || error.message), 'error')
      } finally {
        this.loading = false
      }
    },

    closePromptEditor() {
      // Check for unsaved changes
      if (this.hasUnsavedChanges) {
        const confirmClose = confirm(
          'You have unsaved changes. Are you sure you want to close without saving?\n\n' +
          'Click "OK" to discard changes or "Cancel" to continue editing.'
        )
        
        if (!confirmClose) {
          return // Don't close if user cancels
        }
      }
      
      // Close the editor
      this.editingPrompt = null
      this.originalPrompt = null
    },

    closeAddNewPrompt() {
      this.showAddNewPrompt = false
    },
    
    async openCopyDefinitionModal() {
      if (!this.selectedCheckDefinition) return
      
      this.loading = true
      try {
        // Load full check definition data with requirements
        const response = await api.get(`/comprehensive-check-definitions/${this.selectedCheckDefinition}/`)
        this.fullCheckDefinitionData = response.data
        
        // Pre-fill the form with suggested name
        this.copyDefinitionForm.name = `Copy of ${this.fullCheckDefinitionData.name}`
        this.copyDefinitionForm.description = ''
        
        this.showCopyCheckDefinitionModal = true
        
      } catch (error) {
        console.error('Error loading check definition details:', error)
        this.showMessage('Failed to load check definition details: ' + (error.response?.data?.error || error.message), 'error')
      } finally {
        this.loading = false
      }
    },
    
    closeCopyDefinitionModal() {
      this.showCopyCheckDefinitionModal = false
      this.copyDefinitionForm = {
        name: '',
        description: ''
      }
      this.fullCheckDefinitionData = null
    },
    
    // Create new check definition methods
    openCreateDefinitionModal() {
      this.createDefinitionForm = {
        name: '',
        description: '',
        project_type: 'CERT_REVIEW',
        check_application_type: 'COI_COMPLIANCE',
        document_types_text: ''
      }
      this.showCreateDefinitionModal = true
    },
    
    closeCreateDefinitionModal() {
      this.showCreateDefinitionModal = false
      this.createDefinitionForm = {
        name: '',
        description: '',
        project_type: 'CERT_REVIEW',
        check_application_type: 'COI_COMPLIANCE',
        document_types_text: ''
      }
    },
    
    async createNewCheckDefinition() {
      if (!this.createDefinitionForm.name.trim()) {
        this.showMessage('Please enter a name for the check definition', 'error')
        return
      }
      
      this.loading = true
      try {
        // Parse document types from comma-separated text
        const documentTypes = this.createDefinitionForm.document_types_text
          ? this.createDefinitionForm.document_types_text.split(',').map(t => t.trim().toUpperCase()).filter(t => t)
          : []
        
        const response = await api.post('/comprehensive-check-definitions/', {
          name: this.createDefinitionForm.name.trim(),
          description: this.createDefinitionForm.description.trim() || '',
          project_type: this.createDefinitionForm.project_type,
          check_application_type: this.createDefinitionForm.check_application_type,
          document_types: documentTypes
        })
        
        this.showMessage(
          `✅ Created new check definition: "${this.createDefinitionForm.name}"! You can now add prompts to it.`,
          'success'
        )
        
        // Refresh check definitions
        await this.loadCheckDefinitions()
        
        // Select the new definition
        this.selectedCheckDefinition = response.data.id
        
        // Close modal
        this.closeCreateDefinitionModal()
        
      } catch (error) {
        console.error('Create check definition failed:', error)
        this.showMessage(
          'Failed to create check definition: ' + (error.response?.data?.error || error.response?.data?.detail || error.message), 
          'error'
        )
      } finally {
        this.loading = false
      }
    },
    
    async copyCheckDefinition() {
      if (!this.selectedCheckDefinition || !this.copyDefinitionForm.name.trim()) {
        return
      }
      
      this.loading = true
      try {
        const response = await api.post(`/comprehensive-check-definitions/${this.selectedCheckDefinition}/copy/`, {
          name: this.copyDefinitionForm.name.trim(),
          description: this.copyDefinitionForm.description.trim() || undefined
        })
        
        if (response.data.success) {
          this.showMessage(
            `✅ Successfully copied "${this.fullCheckDefinitionData.name}" with ${response.data.copied_requirements} requirements!`,
            'success'
          )
          
          // Refresh check definitions to show the new copy
          await this.loadCheckDefinitions()
          
          // Close modal
          this.closeCopyDefinitionModal()
          
          // Optionally select the new definition
          this.selectedCheckDefinition = response.data.new_definition.id
          
        } else {
          this.showMessage(`Failed to copy check definition: ${response.data.error}`, 'error')
        }
        
      } catch (error) {
        console.error('Copy check definition failed:', error)
        this.showMessage(
          'Copy failed: ' + (error.response?.data?.error || error.message), 
          'error'
        )
      } finally {
        this.loading = false
      }
    },

    closeTemplateLibrary() {
      this.showTemplateLibrary = false
    },

    // Bulk prompt creation methods
    closeBulkPromptCreator() {
      if (this.bulkPrompts.length > 0) {
        if (confirm('You have unsaved prompts. Are you sure you want to close?')) {
          this.resetBulkCreator()
        }
      } else {
        this.resetBulkCreator()
      }
    },

    resetBulkCreator() {
      this.showBulkPromptCreator = false
      this.bulkModalMinimized = false
      this.bulkInputText = ''
      this.parsedPrompts = []
      this.bulkPrompts = []
      this.bulkCreationProgress = 0
    },

    minimizeBulkModal() {
      this.showBulkPromptCreator = false
      this.bulkModalMinimized = true
    },

    restoreBulkModal() {
      this.bulkModalMinimized = false
      this.showBulkPromptCreator = true
    },

    parseBulkInput() {
      if (!this.bulkInputText.trim()) {
        this.parsedPrompts = []
        return
      }

      let prompts = []
      const text = this.bulkInputText.trim()

      switch (this.bulkInputFormat) {
        case 'line':
          prompts = text.split('\n').filter(line => line.trim())
          break
        case 'numbered':
          prompts = text.split(/\n\d+\.\s*/).filter(line => line.trim())
          break
        case 'bullet':
          prompts = text.split(/\n[•\-*]\s*/).filter(line => line.trim())
          break
        case 'comma':
          prompts = text.split(',').filter(line => line.trim())
          break
      }

      this.parsedPrompts = prompts.map(p => p.trim()).filter(p => p.length > 0)
    },

    handleBulkPaste() {
      // Auto-detect format after paste
      setTimeout(() => {
        const text = this.bulkInputText
        if (text.match(/^\d+\./m)) {
          this.bulkInputFormat = 'numbered'
        } else if (text.match(/^[•\-*]/m)) {
          this.bulkInputFormat = 'bullet'
        } else if (text.includes(',') && !text.includes('\n')) {
          this.bulkInputFormat = 'comma'
        } else {
          this.bulkInputFormat = 'line'
        }
        this.parseBulkInput()
      }, 100)
    },

    clearBulkInput() {
      this.bulkInputText = ''
      this.parsedPrompts = []
    },

    formatBulkInput() {
      if (this.parsedPrompts.length === 0) return
      
      let formatted = ''
      this.parsedPrompts.forEach((prompt, index) => {
        formatted += `${index + 1}. ${prompt}\n`
      })
      
      this.bulkInputText = formatted.trim()
      this.bulkInputFormat = 'numbered'
    },

    addAllParsedPrompts() {
      this.parsedPrompts.forEach(prompt => {
        if (prompt.trim()) {
          this.addPromptToBulkQueue(prompt.trim())
        }
      })
      this.clearBulkInput()
      this.showMessage(`Added ${this.parsedPrompts.length} prompts to the queue`, 'success')
    },

    addSingleParsedPrompt(index) {
      const prompt = this.parsedPrompts[index]
      if (prompt && prompt.trim()) {
        this.addPromptToBulkQueue(prompt.trim())
        this.parsedPrompts.splice(index, 1)
        this.showMessage('Added prompt to queue', 'success')
      }
    },

    addPromptToBulkQueue(promptText) {
      const name = this.generatePromptName(promptText)
      const code = this.generatePromptCode(name)
      
      const newPrompt = {
        name: name,
        code: code,
        type: this.bulkGlobalSettings.type,
        severity: this.bulkGlobalSettings.severity,
        check_definition_id: this.bulkGlobalSettings.check_definition_id,
        prompt_template: promptText,
        description: `Bulk created: ${promptText.substring(0, 100)}${promptText.length > 100 ? '...' : ''}`,
        expanded: false,
        processing: false,
        completed: false,
        error: null
      }
      
      this.bulkPrompts.push(newPrompt)
    },

    generatePromptName(promptText) {
      // Generate a concise name from the prompt text
      let name = promptText.trim()
      
      // Remove common prefixes
      name = name.replace(/^(check if|verify that|confirm that|ensure that)\s*/i, '')
      
      // Capitalize first letter
      name = name.charAt(0).toUpperCase() + name.slice(1)
      
      // Truncate if too long
      if (name.length > 50) {
        name = name.substring(0, 47) + '...'
      }
      
      return name
    },

    generatePromptCode(name) {
      return name
        .toLowerCase()
        .replace(/[^a-z0-9\s]/g, '')
        .replace(/\s+/g, '_')
        .substring(0, 50) + '_' + Date.now().toString().slice(-4)
    },

    editBulkPrompt(index) {
      this.bulkPrompts[index].expanded = !this.bulkPrompts[index].expanded
    },

    removeBulkPrompt(index) {
      this.bulkPrompts.splice(index, 1)
    },

    clearAllBulkPrompts() {
      if (confirm('Are you sure you want to clear all prompts from the queue?')) {
        this.bulkPrompts = []
        this.bulkCreationProgress = 0
      }
    },

    async processBulkPrompts() {
      if (this.bulkPrompts.length === 0) return
      
      this.loading = true
      this.bulkCreationProgress = 0
      let successCount = 0
      let errorCount = 0
      
      for (let i = 0; i < this.bulkPrompts.length; i++) {
        const prompt = this.bulkPrompts[i]
        prompt.processing = true
        
        try {
          // Validate required fields
          if (!prompt.check_definition_id) {
            throw new Error('Check definition is required')
          }
          
          const promptData = {
            name: prompt.name,
            code: prompt.code,
            type: prompt.type,
            prompt_template: prompt.prompt_template,
            check_definition_id: parseInt(prompt.check_definition_id),
            severity: prompt.severity,
            description: prompt.description
          }
          
          await api.post('/prompts/create/', promptData)
          
          prompt.processing = false
          prompt.completed = true
          successCount++
          
        } catch (error) {
          prompt.processing = false
          prompt.error = error.response?.data?.error || error.message
          errorCount++
        }
        
        this.bulkCreationProgress = ((i + 1) / this.bulkPrompts.length) * 100
        
        // Small delay to show progress
        await new Promise(resolve => setTimeout(resolve, 200))
      }
      
      this.loading = false
      
      if (successCount > 0) {
        this.showMessage(`✅ Successfully created ${successCount} prompts${errorCount > 0 ? ` (${errorCount} failed)` : ''}`, 'success')
        // Reload prompt library if it's visible
        if (this.promptLibrary.length > 0) {
          await this.loadPromptLibrary()
        }
      } else {
        this.showMessage(`❌ Failed to create prompts. Check the error messages above.`, 'error')
      }
    },

    // Structured input methods
    addNewStructuredRow() {
      this.structuredRows.push({
        name: '',
        prompt_template: '',
        type: this.bulkGlobalSettings.type,
        severity: this.bulkGlobalSettings.severity,
        description: '',
        expected_value: '',
        check_definition_id: this.bulkGlobalSettings.check_definition_id
      })
    },

    removeStructuredRow(index) {
      if (this.structuredRows.length > 1) {
        this.structuredRows.splice(index, 1)
      }
    },

    clearStructuredRows() {
      this.structuredRows = [{
        name: '',
        prompt_template: '',
        type: this.bulkGlobalSettings.type,
        severity: this.bulkGlobalSettings.severity,
        description: '',
        expected_value: '',
        check_definition_id: this.bulkGlobalSettings.check_definition_id
      }]
    },

    handleTabNavigation(event, rowIndex, colIndex) {
      // Enhanced tab navigation for structured input
      if (event.key === 'Tab' && !event.shiftKey) {
        // Moving forward
        if (colIndex === 5) { // Last editable column
          event.preventDefault()
          if (rowIndex === this.structuredRows.length - 1) {
            this.addNewStructuredRow()
          }
          // Focus will naturally move to next row
        }
      }
    },

    addStructuredToQueue() {
      const validRows = this.structuredRows.filter(row => 
        row.name.trim() && 
        row.prompt_template.trim() && 
        row.type &&
        this.bulkGlobalSettings.check_definition_id
      )
      
      validRows.forEach(row => {
        const code = this.generatePromptCode(row.name)
        const newPrompt = {
          name: row.name.trim(),
          code: code,
          type: row.type,
          severity: row.severity,
          check_definition_id: this.bulkGlobalSettings.check_definition_id,
          prompt_template: row.prompt_template.trim(),
          description: row.description.trim() || `Structured input: ${row.prompt_template.substring(0, 100)}${row.prompt_template.length > 100 ? '...' : ''}`,
          expected_value: row.expected_value.trim(),
          expanded: false,
          processing: false,
          completed: false,
          error: null
        }
        this.bulkPrompts.push(newPrompt)
      })
      
      this.clearStructuredRows()
      this.showMessage(`Added ${validRows.length} prompts to the queue`, 'success')
    },

    // CSV Import methods
    closeBulkImportModal() {
      this.showBulkImportModal = false
      this.resetImportModal()
    },

    resetImportModal() {
      this.importStep = 1
      this.selectedImportFile = null
      this.isDragOver = false
      this.parsing = false
      this.importing = false
      this.importColumns = []
      this.importData = []
      this.columnMapping = {}
      this.validImportRows = []
      this.invalidImportRows = []
      this.importErrors = []
      this.importResults = {
        success: 0,
        errors: 0,
        errorDetails: []
      }
    },

    handleFileDrop(event) {
      this.isDragOver = false
      const files = event.dataTransfer.files
      if (files.length > 0) {
        this.selectedImportFile = files[0]
      }
    },

    handleFileSelect(event) {
      const files = event.target.files
      if (files.length > 0) {
        this.selectedImportFile = files[0]
      }
    },

    clearSelectedFile() {
      this.selectedImportFile = null
      this.$refs.fileInput.value = ''
    },

    formatFileSize(bytes) {
      if (bytes === 0) return '0 Bytes'
      const k = 1024
      const sizes = ['Bytes', 'KB', 'MB', 'GB']
      const i = Math.floor(Math.log(bytes) / Math.log(k))
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
    },

    async parseImportFile() {
      if (!this.selectedImportFile) return
      
      this.parsing = true
      try {
        console.log('Reading file:', this.selectedImportFile.name, 'size:', this.selectedImportFile.size)
        const text = await this.readFileAsText(this.selectedImportFile)
        console.log('File text length:', text.length)
        const rows = this.parseCSV(text)
        
        if (rows.length === 0) {
          throw new Error('No data found in file')
        }
        
        console.log('Total rows parsed:', rows.length)
        this.importColumns = rows[0] // First row as headers
        this.importData = rows.slice(1) // Rest as data
        
        console.log('Import columns:', this.importColumns)
        console.log('Import data rows:', this.importData.length)
        console.log('First few data rows:', this.importData.slice(0, 3))
        
        // Auto-map columns based on header names
        this.autoMapColumns()
        
        console.log('Column mapping result:', this.columnMapping)
        
        this.importStep = 2
        
      } catch (error) {
        console.error('Parse file error:', error)
        this.showMessage(`Failed to parse file: ${error.message}`, 'error')
      } finally {
        this.parsing = false
      }
    },

    readFileAsText(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = e => resolve(e.target.result)
        reader.onerror = () => reject(new Error('Failed to read file'))
        reader.readAsText(file)
      })
    },

    parseCSV(text) {
      console.log('Parsing CSV text:', text.substring(0, 500) + '...')
      const lines = text.split(/\r?\n/)
      console.log('Split into lines:', lines.length, 'lines')
      const result = []
      
      for (let i = 0; i < lines.length; i++) {
        const line = lines[i]
        console.log(`Line ${i}:`, line)
        if (line.trim()) {
          // More robust CSV parsing that handles quoted fields
          const row = this.parseCSVLine(line)
          console.log(`Parsed row ${i}:`, row)
          result.push(row)
        } else {
          console.log(`Skipping empty line ${i}`)
        }
      }
      
      console.log('Final parsed result:', result)
      return result
    },

    parseCSVLine(line) {
      const result = []
      let current = ''
      let inQuotes = false
      
      for (let i = 0; i < line.length; i++) {
        const char = line[i]
        
        if (char === '"') {
          inQuotes = !inQuotes
        } else if (char === ',' && !inQuotes) {
          result.push(current.trim())
          current = ''
        } else {
          current += char
        }
      }
      
      // Add the last field
      result.push(current.trim())
      
      return result
    },

    autoMapColumns() {
      this.columnMapping = {}
      
      // Auto-map based on column name similarity
      this.importFields.forEach(field => {
        const matchingColumn = this.importColumns.find(col => 
          col.toLowerCase().includes(field.key.toLowerCase()) ||
          field.key.toLowerCase().includes(col.toLowerCase()) ||
          (field.key === 'check_definition_name' && col.toLowerCase().includes('definition')) ||
          (field.key === 'prompt_template' && (col.toLowerCase().includes('prompt') && col.toLowerCase().includes('template')))
        )
        
        if (matchingColumn) {
          this.columnMapping[field.key] = matchingColumn
        }
      })
    },

    getSampleData(fieldKey) {
      const columnName = this.columnMapping[fieldKey]
      if (!columnName || this.importData.length === 0) return ''
      
      const columnIndex = this.importColumns.indexOf(columnName)
      if (columnIndex === -1) return ''
      
      const sampleValue = this.importData[0][columnIndex] || ''
      return sampleValue.length > 30 ? sampleValue.substring(0, 30) + '...' : sampleValue
    },

    validateImportData() {
      this.validImportRows = []
      this.invalidImportRows = []
      this.importErrors = []
      
      console.log('Validating import data:', {
        importData: this.importData,
        columnMapping: this.columnMapping,
        availableCheckDefinitions: this.availableCheckDefinitions.map(def => def.name)
      })
      
      this.importData.forEach((row, index) => {
        // Skip empty rows
        if (!row || row.every(cell => !cell || !cell.trim())) {
          console.log(`Skipping empty row ${index + 2}`)
          return
        }
        
        const mappedRow = {}
        const errors = []
        
        // Map columns to fields
        this.importFields.forEach(field => {
          const columnName = this.columnMapping[field.key]
          if (columnName) {
            const columnIndex = this.importColumns.indexOf(columnName)
            mappedRow[field.key] = columnIndex !== -1 ? (row[columnIndex] || '').trim() : ''
          } else {
            mappedRow[field.key] = ''
          }
        })
        
        console.log(`Row ${index + 2} mapped data:`, mappedRow)
        console.log(`Row ${index + 2} raw row data:`, row)
        console.log(`check_definition_name mapping:`, this.columnMapping.check_definition_name, 'column index:', this.importColumns.indexOf(this.columnMapping.check_definition_name))
        
        // Validate required fields
        this.importFields.forEach(field => {
          if (field.required && !mappedRow[field.key]) {
            errors.push(`${field.label} is required`)
          }
        })
        
        // Validate specific field formats
        if (mappedRow.type && !['text_match', 'coverage_amount', 'checkbox_verification', 'entity_verification', 'unknown', 'subject_verification'].includes(mappedRow.type)) {
          errors.push(`Invalid type: ${mappedRow.type}`)
        }
        
        if (mappedRow.severity && !['LOW', 'MEDIUM', 'HIGH', 'CRITICAL'].includes(mappedRow.severity)) {
          errors.push(`Invalid severity: ${mappedRow.severity}`)
        }
        
        // Find matching check definition
        if (mappedRow.check_definition_name) {
          // Handle both string array and object array formats
          let checkDef
          if (this.availableCheckDefinitions.length > 0 && typeof this.availableCheckDefinitions[0] === 'string') {
            // Array of strings format (fallback)
            checkDef = this.availableCheckDefinitions.find(defName => 
              defName.toLowerCase() === mappedRow.check_definition_name.toLowerCase()
            )
            if (checkDef) {
              // For string format, we need to find the actual ID from the full check definitions
              // For now, we'll use the name as a placeholder
              mappedRow.check_definition_id = checkDef
            }
          } else {
            // Array of objects format (expected)
            checkDef = this.availableCheckDefinitions.find(def => 
              def && def.name && def.name.toLowerCase() === mappedRow.check_definition_name.toLowerCase()
            )
            if (checkDef) {
              mappedRow.check_definition_id = checkDef.id
            }
          }
          
          if (!checkDef) {
            errors.push(`Check definition not found: ${mappedRow.check_definition_name}. Available: ${this.availableCheckDefinitions.map(def => typeof def === 'string' ? def : def.name).join(', ')}`)
          }
        }
        
        console.log(`Row ${index + 2} validation errors:`, errors)
        
        if (errors.length === 0) {
          this.validImportRows.push(mappedRow)
        } else {
          this.invalidImportRows.push(mappedRow)
          errors.forEach(error => {
            this.importErrors.push({ row: index + 2, message: error }) // +2 for header and 0-based index
          })
        }
      })
      
      console.log('Validation complete:', {
        validRows: this.validImportRows.length,
        invalidRows: this.invalidImportRows.length,
        errors: this.importErrors
      })
      
      this.importStep = 3
    },

    async executeImport() {
      if (this.validImportRows.length === 0) return
      
      this.importing = true
      let successCount = 0
      let errorCount = 0
      const errorDetails = []
      
      for (const row of this.validImportRows) {
        try {
          const promptData = {
            name: row.name,
            code: row.code,
            type: row.type,
            prompt_template: row.prompt_template || `Check for: ${row.name}`,
            check_definition_id: parseInt(row.check_definition_id),
            severity: row.severity,
            description: row.description || `Imported: ${row.name}`,
            validation_logic: {
              type: row.type,
              expected_value: row.expected_value || '',
              case_sensitive: row.case_sensitive === 'true' || row.case_sensitive === true
            }
          }
          
          await api.post('/prompts/create/', promptData)
          successCount++
          
        } catch (error) {
          errorCount++
          errorDetails.push({
            name: row.name,
            error: error.response?.data?.error || error.message
          })
        }
      }
      
      this.importResults = {
        success: successCount,
        errors: errorCount,
        errorDetails
      }
      
      this.importing = false
      this.importStep = 4
      
      if (successCount > 0) {
        // Reload prompt library if it's visible
        if (this.promptLibrary.length > 0) {
          await this.loadPromptLibrary()
        }
      }
    },

    async exportPrompts() {
      this.loading = true
      try {
        const response = await api.get('/prompts/export/')
        const blob = new Blob([response.data], { type: 'text/csv' })
        const url = window.URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = 'prompt_library.csv'
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        window.URL.revokeObjectURL(url)
        this.showMessage('Prompts exported successfully!', 'success')
      } catch (error) {
        console.error('Export failed:', error)
        this.showMessage('Export failed: ' + (error.response?.data?.error || error.message), 'error')
      } finally {
        this.loading = false
      }
    },

         getFilteredPrompts() {
       // Safety check: ensure promptLibrary is an array and filter out invalid items
       let filtered = Array.isArray(this.promptLibrary) 
         ? this.promptLibrary.filter(prompt => prompt && prompt.id)
         : []
         
       // Filter by active status unless showInactivePrompts is enabled
       if (!this.showInactivePrompts) {
         filtered = filtered.filter(prompt => prompt && prompt.is_active)
       }
         
       if (this.libraryFilter) {
         filtered = filtered.filter(prompt => 
           prompt && prompt.check_definition_id == this.libraryFilter
         )
       }
       if (this.librarySearch) {
         const searchLower = this.librarySearch.toLowerCase()
         filtered = filtered.filter(prompt => {
           if (!prompt) return false
           return (
             (prompt.name || '').toLowerCase().includes(searchLower) || 
             (prompt.code || '').toLowerCase().includes(searchLower) || 
             (prompt.description || '').toLowerCase().includes(searchLower) || 
             (prompt.prompt_template || '').toLowerCase().includes(searchLower) ||
             (prompt.check_definition_name || '').toLowerCase().includes(searchLower)
           )
         })
       }
       return filtered
     },

     async loadEffectivenessData() {
       this.loading = true
       try {
         const response = await api.get('/prompt-effectiveness/', {
           params: {
             days: this.effectivenessPeriod
           }
         })
         this.effectivenessData = response.data
         this.showMessage(`Loaded effectiveness data for ${this.effectivenessPeriod} day period.`, 'info')
       } catch (error) {
         console.error('Failed to load effectiveness data:', error)
         this.showMessage('Failed to load effectiveness data: ' + (error.response?.data?.error || error.message), 'error')
              } finally {
         this.loading = false
       }
     },

     // Quick create simple prompt methods
     async createSimplePrompt() {
       if (!this.canCreateQuickPrompt) {
         this.showMessage('Please fill in all required fields', 'error');
         return;
       }
       
       this.loading = true;
       try {
         const code = this.quickPrompt.name
           .toLowerCase()
           .replace(/[^a-z0-9\s]/g, '')
           .replace(/\s+/g, '_')
           .substring(0, 50);
         
         const promptData = {
           name: this.quickPrompt.name.trim(),
           code: code,
           type: this.quickPrompt.type,
           prompt_template: this.quickPrompt.prompt_template.trim(),
           check_definition_id: parseInt(this.quickPrompt.check_definition_id),
           severity: this.quickPrompt.severity,
           description: `Simple prompt: ${this.quickPrompt.prompt_template.substring(0, 100)}${this.quickPrompt.prompt_template.length > 100 ? '...' : ''}`
         };
         
         console.log('Creating prompt with data:', promptData);
         
         const response = await api.post('/prompts/create/', promptData);
         
         if (response.data) {
           this.showMessage(`Prompt "${this.quickPrompt.name}" created successfully!`, 'success');
           this.resetQuickPrompt();
           // Reload prompt library if it's visible
           if (this.promptLibrary.length > 0) {
             await this.loadPromptLibrary();
           }
         }
       } catch (error) {
         console.error('Create prompt error:', error);
         console.error('Error details:', {
           status: error.response?.status,
           statusText: error.response?.statusText,
           data: error.response?.data,
           config: error.config
         });
         
         let errorMessage = 'Failed to create prompt';
         if (error.response?.status === 405) {
           errorMessage = 'Method not allowed - please check if you are logged in';
         } else if (error.response?.data?.error) {
           errorMessage = `Failed to create prompt: ${error.response.data.error}`;
         } else {
           errorMessage = `Failed to create prompt: ${error.message}`;
         }
         
         this.showMessage(errorMessage, 'error');
       } finally {
         this.loading = false;
       }
     },

     async createAndEnhancePrompt() {
       if (!this.canCreateQuickPrompt) return;
       
       try {
         // First create the simple prompt
         await this.createSimplePrompt();
         
         // Wait a moment for the creation to complete
         await new Promise(resolve => setTimeout(resolve, 500));
         
         // Find the newly created prompt by code
         const code = this.quickPrompt.name
           .toLowerCase()
           .replace(/[^a-z0-9\s]/g, '')
           .replace(/\s+/g, '_')
           .substring(0, 50);
         
         // Reload prompt library to get the new prompt
         await this.loadPromptLibrary();
         const newPrompt = this.promptLibrary.find(p => p.code === code);
         
         if (newPrompt) {
           // Now enhance it
           const enhanceResponse = await api.post('/enhance-prompts/', {
             action: 'enhance',
             requirement_id: newPrompt.id
           });
           
           if (enhanceResponse.data.success) {
             this.showMessage(`Prompt "${this.quickPrompt.name}" created and enhanced successfully!`, 'success');
             // Reload library again to show enhanced version
             await this.loadPromptLibrary();
           } else {
             this.showMessage('Prompt created but enhancement failed: ' + enhanceResponse.data.error, 'warning');
           }
         } else {
           this.showMessage('Prompt created but could not find it for enhancement', 'warning');
         }
         
       } catch (error) {
         console.error('Create and enhance error:', error);
         this.showMessage('Failed to create and enhance prompt: ' + (error.response?.data?.error || error.message), 'error');
       }
     },

     cancelQuickCreate() {
       this.resetQuickPrompt();
     },

    resetQuickPrompt() {
      this.showQuickCreate = false;
      this.quickPrompt = {
        name: '',
        type: '',
        check_definition_id: '',
        severity: 'MEDIUM',
        prompt_template: ''
      };
    },

    // Toggle functions for expandable sections (to prevent console errors)
    toggleAboutSection() {
      this.showAboutSection = !this.showAboutSection;
    },

    toggleHowToGuide() {
      this.showHowToGuide = !this.showHowToGuide;
    },

    toggleExampleSection() {
      this.showExampleSection = !this.showExampleSection;
    },

    
    // ============================================================================
    // FEEDBACK-DRIVEN OPTIMIZATION METHODS
    // ============================================================================
    
    async analyzeFeedbackPatterns() {
      this.loading = true;
      try {
        const response = await api.get('/feedback/organization-summary/?days_back=30');
        this.feedbackAnalysisResults = response.data;
        
        this.showMessage(
          `Feedback analysis complete: Found ${this.feedbackAnalysisResults.requirements_needing_improvement} requirements that can be optimized`, 
          'success'
        );
        
      } catch (error) {
        console.error('Feedback analysis failed:', error);
        this.showMessage('Feedback analysis failed: ' + (error.response?.data?.error || error.message), 'error');
      } finally {
        this.loading = false;
      }
    },
    
    async runAutoOptimization() {
      if (!confirm('This will automatically generate improved prompts based on user feedback. Continue?')) {
        return;
      }
      
      this.loading = true;
      try {
        const response = await api.post('/prompts/auto-improve/', {
          accuracy_threshold: 0.7,
          organization_only: true
        });
        
        const results = response.data.results;
        this.showMessage(
          `Auto-optimization complete: ${results.improvements_generated} improvements generated, ${results.improvements_applied} applied`,
          'success'
        );
        
        // Refresh both analyses
        await this.runDryRunAnalysis();
        await this.analyzeFeedbackPatterns();
        
      } catch (error) {
        console.error('Auto-optimization failed:', error);
        this.showMessage('Auto-optimization failed: ' + (error.response?.data?.error || error.message), 'error');
      } finally {
        this.loading = false;
      }
    },
    
    async generateFeedbackEnhancement(requirementId) {
      this.loading = true;
      try {
        const response = await api.post(`/requirements/${requirementId}/enhance-with-feedback/`);
        
        if (response.data.success) {
          this.showMessage(
            `✨ Feedback-driven enhancement generated! Addressed ${response.data.issues_addressed.length} specific issues.`,
            'success'
          );
          
          // Refresh analyses
          await this.runDryRunAnalysis();
          await this.analyzeFeedbackPatterns();
        }
        
      } catch (error) {
        console.error('Feedback enhancement failed:', error);
        this.showMessage('Feedback enhancement failed: ' + (error.response?.data?.error || error.message), 'error');
      } finally {
        this.loading = false;
      }
    },
    
    async analyzeSingleRequirementFeedback(requirementId) {
      try {
        const response = await api.get(`/requirements/${requirementId}/feedback-enhancement-status/`);
        const analysis = response.data.feedback_analysis;
        
        // Show detailed feedback analysis in a modal or alert
        const message = `
Feedback Analysis for Requirement:

• Total Feedback: ${analysis.feedback_count}
• Improvement Needed: ${analysis.improvement_needed ? 'Yes' : 'No'}
• Top Issues: ${analysis.improvement_suggestions.slice(0, 3).join(', ')}

Click "Generate Feedback-Driven Enhancement" to create optimized prompt.
        `;
        
        alert(message);
        
      } catch (error) {
        console.error('Single requirement analysis failed:', error);
        this.showMessage('Analysis failed: ' + (error.response?.data?.error || error.message), 'error');
      }
    },
    
    formatIssueName(issue) {
      return issue.replace(/_/g, ' ').toLowerCase()
        .replace(/\b\w/g, l => l.toUpperCase());
    },
    
    getIssueDescription(issue) {
      const descriptions = {
        'OVERTHINKING': 'Model over-analyzes simple cases instead of being direct',
        'ENTITY_CONFUSION': 'Model struggles with entity name variations and recognition',
        'POLICY_MISREAD': 'Model misinterprets policy presence or coverage details',
        'FORMAT_ISSUE': 'Model has trouble with document format or structure',
        'CONTEXT_MISSING': 'Model misses important contextual information',
        'FALSE_POSITIVE': 'Model incorrectly flags requirements as passing',
        'FALSE_NEGATIVE': 'Model incorrectly flags requirements as failing'
      };
      return descriptions[issue] || 'Issue requiring attention';
    },

    // Table view methods
    sortTable(field) {
      if (this.tableSortField === field) {
        this.tableSortDirection = this.tableSortDirection === 'asc' ? 'desc' : 'asc'
      } else {
        this.tableSortField = field
        this.tableSortDirection = 'asc'
      }
    },

    getSortIcon(field) {
      if (this.tableSortField !== field) {
        return 'fas fa-sort text-muted'
      }
      return this.tableSortDirection === 'asc' ? 'fas fa-sort-up text-primary' : 'fas fa-sort-down text-primary'
    },

    expandPromptRow(promptId) {
      if (!promptId) return
      
      const index = this.expandedRows.indexOf(promptId)
      if (index > -1) {
        this.expandedRows.splice(index, 1)
      } else {
        this.expandedRows.push(promptId)
      }
    },

    formatType(type) {
      const typeMap = {
        'text_match': 'Text Match',
        'coverage_amount': 'Coverage Amount',
        'date_check': 'Date Check',
        'entity_verification': 'Entity Verification',
        'checkbox_verification': 'Checkbox/Y-N',
        'custom': 'Custom'
      }
      return typeMap[type] || type
    },

    truncateText(text, maxLength) {
      if (!text) return ''
      if (text.length <= maxLength) return text
      return text.substring(0, maxLength) + '...'
    }
   }
 }
</script>

<style scoped>
.prompt-enhancement-manager {
  padding: 20px;
  max-width: 1200px;
  margin: 0 auto;
}

.header-section {
  margin-bottom: 30px;
  text-align: center;
}

.header-section h2 {
  color: #2c3e50;
  margin-bottom: 10px;
}

.description {
  color: #7f8c8d;
  font-size: 16px;
  max-width: 800px;
  margin: 0 auto;
}

/* ============================================
   NEW WORKFLOW SECTION STYLES
   ============================================ */

.workflow-section {
  background: white;
  border-radius: 12px;
  padding: 24px;
  margin-bottom: 24px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.08);
  border: 1px solid #e9ecef;
}

.workflow-section.collapsed {
  padding-bottom: 0;
}

.step-header {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  margin-bottom: 20px;
}

.step-header.clickable {
  cursor: pointer;
  padding: 8px;
  margin: -8px;
  border-radius: 8px;
  transition: background-color 0.2s;
}

.step-header.clickable:hover {
  background: #f8f9fa;
}

.step-number {
  width: 40px;
  height: 40px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  font-size: 18px;
  flex-shrink: 0;
}

.step-info {
  flex: 1;
}

.step-info h3 {
  margin: 0 0 4px 0;
  color: #2c3e50;
  font-size: 1.2rem;
}

.step-info p {
  margin: 0;
  color: #6c757d;
  font-size: 0.9rem;
}

.step-actions {
  display: flex;
  gap: 8px;
}

.expand-indicator {
  color: #6c757d;
  padding: 8px;
}

/* STEP 1: Definition Selector */
.step-1-section {
  border-left: 4px solid #667eea;
}

.definition-selector {
  margin-bottom: 20px;
}

.selector-row {
  display: flex;
  gap: 12px;
  align-items: center;
  flex-wrap: wrap;
}

.selector-main {
  flex: 1;
  min-width: 300px;
}

.definition-dropdown {
  font-size: 1rem;
  padding: 12px 16px;
  border-radius: 8px;
  border: 2px solid #e9ecef;
  transition: border-color 0.2s;
}

.definition-dropdown:focus {
  border-color: #667eea;
  outline: none;
}

.selector-actions {
  display: flex;
  gap: 8px;
}

/* Selected Definition Card */
.selected-definition-card {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-radius: 12px;
  padding: 20px;
  border-left: 4px solid #27ae60;
}

.definition-header {
  display: flex;
  gap: 16px;
  align-items: flex-start;
  margin-bottom: 12px;
}

.definition-icon {
  font-size: 2rem;
}

.definition-details h4 {
  margin: 0 0 4px 0;
  color: #2c3e50;
}

.definition-details .definition-description {
  margin: 0;
  color: #6c757d;
  font-size: 0.9rem;
}

.definition-meta {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.meta-badge {
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 500;
}

.meta-badge.type {
  background: #e3f2fd;
  color: #1976d2;
}

.meta-badge.prompts {
  background: #e8f5e9;
  color: #2e7d32;
}

.meta-badge.app-type {
  background: #f3e5f5;
  color: #7b1fa2;
}

.empty-definition-state {
  text-align: center;
  padding: 40px;
  color: #6c757d;
}

.empty-definition-state .empty-icon {
  font-size: 3rem;
  margin-bottom: 16px;
}

/* STEP 2: Quick Create */
.step-2-section {
  border-left: 4px solid #27ae60;
}

.quick-create-inline {
  background: #f8f9fa;
  border-radius: 12px;
  padding: 20px;
}

.quick-create-header-inline {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.quick-create-header-inline h4 {
  margin: 0;
  color: #2c3e50;
}

.helper-text {
  color: #6c757d;
  font-size: 0.85rem;
}

.quick-form-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
  margin-bottom: 16px;
}

.quick-form-actions {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  align-items: center;
  padding-top: 16px;
  border-top: 1px solid #e9ecef;
}

.bulk-actions-group {
  display: flex;
  gap: 8px;
  margin-left: auto;
}

/* STEP 3: Definition Prompts */
.step-3-section {
  border-left: 4px solid #3498db;
}

.definition-prompts-container {
  background: #f8f9fa;
  border-radius: 12px;
  padding: 20px;
}

.prompts-controls {
  display: flex;
  gap: 12px;
  margin-bottom: 16px;
  flex-wrap: wrap;
}

.prompts-controls .search-input {
  flex: 1;
  min-width: 200px;
}

.prompts-controls .filter-select {
  width: 160px;
}

.inactive-toggle-inline {
  display: flex;
  align-items: center;
  gap: 8px;
}

.loading-prompts {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 40px;
  color: #6c757d;
}

.spinner {
  width: 24px;
  height: 24px;
  border: 3px solid #e9ecef;
  border-top-color: #667eea;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.empty-prompts-state {
  text-align: center;
  padding: 40px;
  color: #6c757d;
}

.empty-prompts-state .empty-icon {
  font-size: 3rem;
  margin-bottom: 16px;
}

.empty-prompts-state h4 {
  margin: 0 0 8px 0;
  color: #495057;
}

/* Prompts Table */
.prompts-table-container {
  overflow-x: auto;
}

.prompts-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.9rem;
}

.prompts-table th {
  background: #e9ecef;
  padding: 12px 8px;
  text-align: left;
  font-weight: 600;
  color: #495057;
  border-bottom: 2px solid #dee2e6;
}

.prompts-table td {
  padding: 12px 8px;
  border-bottom: 1px solid #e9ecef;
  vertical-align: middle;
}

.prompt-row-item {
  transition: background-color 0.2s;
}

.prompt-row-item:hover {
  background: white;
}

.prompt-row-item.inactive-row {
  opacity: 0.6;
  background: #f8f9fa;
}

.prompt-row-item.enhanced-row {
  border-left: 3px solid #27ae60;
}

.col-active { width: 60px; }
.col-name { min-width: 200px; }
.col-type { width: 120px; }
.col-severity { width: 100px; }
.col-status { width: 100px; }
.col-actions { width: 120px; }

.name-cell-content {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.name-cell-content strong {
  color: #2c3e50;
}

.code-badge {
  font-size: 0.75rem;
  color: #6c757d;
  background: #e9ecef;
  padding: 2px 6px;
  border-radius: 4px;
  font-family: monospace;
}

.type-badge-sm {
  background: #f3e5f5;
  color: #7b1fa2;
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 500;
}

.severity-badge-sm {
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 500;
  text-transform: uppercase;
}

.severity-badge-sm.low { background: #e8f5e9; color: #2e7d32; }
.severity-badge-sm.medium { background: #fff3e0; color: #f57c00; }
.severity-badge-sm.high { background: #ffebee; color: #d32f2f; }
.severity-badge-sm.critical { background: #fce4ec; color: #c2185b; }

.status-enhanced {
  color: #27ae60;
  font-size: 0.85rem;
}

.status-basic {
  color: #6c757d;
  font-size: 0.85rem;
}

.action-buttons {
  display: flex;
  gap: 4px;
}

.prompts-summary {
  margin-top: 12px;
  text-align: right;
  color: #6c757d;
  font-size: 0.85rem;
}

/* STEP 4: Optimize Section */
.step-4-section {
  border-left: 4px solid #f39c12;
}

.step-4-section.collapsed .step-header {
  margin-bottom: 0;
}

.optimize-content {
  padding-top: 20px;
}

.optimize-actions {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 20px;
}

.analysis-results-compact {
  background: #f8f9fa;
  border-radius: 12px;
  padding: 20px;
}

.stats-row {
  display: flex;
  gap: 24px;
  margin-bottom: 20px;
}

.stat-item {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.stat-item .stat-value {
  font-size: 2rem;
  font-weight: bold;
  color: #3498db;
}

.stat-item.needs .stat-value {
  color: #e74c3c;
}

.stat-item.enhanced .stat-value {
  color: #27ae60;
}

.stat-item .stat-label {
  font-size: 0.85rem;
  color: #6c757d;
}

.enhancement-list h5 {
  margin: 0 0 12px 0;
  color: #495057;
}

.enhancement-items {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.enhancement-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px;
  background: white;
  border-radius: 8px;
  border-left: 3px solid #e74c3c;
}

.enhancement-item .item-info {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.enhancement-item .chars-badge {
  font-size: 0.75rem;
  color: #6c757d;
  background: #e9ecef;
  padding: 2px 6px;
  border-radius: 4px;
}

.enhancement-item .item-actions {
  display: flex;
  gap: 4px;
}

.more-items {
  text-align: center;
  color: #6c757d;
  font-size: 0.85rem;
  padding: 8px;
}

/* Library Section */
.library-section {
  border-left: 4px solid #9b59b6;
}

.library-content {
  padding-top: 20px;
}

.library-controls-row {
  display: flex;
  gap: 12px;
  margin-bottom: 20px;
}

/* Responsive */
@media (max-width: 768px) {
  .prompt-enhancement-manager {
    padding: 15px;
  }
  
  .workflow-section {
    padding: 16px;
    margin-bottom: 16px;
  }
  
  .step-header {
    flex-direction: column;
    gap: 12px;
  }
  
  .step-number {
    width: 32px;
    height: 32px;
    font-size: 14px;
  }
  
  .selector-row {
    flex-direction: column;
  }
  
  .selector-main {
    min-width: 100%;
  }
  
  .selector-actions {
    width: 100%;
    justify-content: stretch;
  }
  
  .selector-actions .btn {
    flex: 1;
  }
  
  .quick-form-grid {
    grid-template-columns: 1fr;
  }
  
  .quick-form-actions {
    flex-direction: column;
    align-items: stretch;
  }
  
  .bulk-actions-group {
    margin-left: 0;
    justify-content: stretch;
  }
  
  .prompts-controls {
    flex-direction: column;
  }
  
  .prompts-controls .search-input,
  .prompts-controls .filter-select {
    width: 100%;
  }
  
  .optimize-actions {
    flex-direction: column;
  }
  
  .stats-row {
    flex-wrap: wrap;
    justify-content: center;
  }
  
  .controls {
    flex-direction: column;
    gap: 10px;
  }
}


/* Selected Definition Info */
.selected-definition-info {
  margin-bottom: 20px;
}

.definition-card {
  background: white;
  padding: 20px;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  border-left: 4px solid #27ae60;
}

.definition-card h4 {
  color: #2c3e50;
  margin: 0 0 10px 0;
  font-size: 18px;
}

.definition-description {
  color: #7f8c8d;
  margin: 0 0 15px 0;
  font-style: italic;
}

.definition-stats {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

.stat-badge {
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: bold;
  color: white;
}

.stat-badge.needs-enhancement {
  background: #e74c3c;
}

.stat-badge.already-enhanced {
  background: #27ae60;
}

.stat-badge.total {
  background: #3498db;
}

/* Analysis Section - PRIMARY FUNCTIONALITY */
.analysis-section {
  background: #f8f9fa;
  padding: 20px;
  border-radius: 8px;
  margin-bottom: 30px;
}

.primary-section {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 16px;
  padding: 30px;
  margin-bottom: 40px;
  box-shadow: 0 8px 24px rgba(0,0,0,0.15);
}

.primary-section .controls {
  background: rgba(255,255,255,0.95);
  padding: 20px;
  border-radius: 12px;
  box-shadow: 0 4px 16px rgba(0,0,0,0.1);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255,255,255,0.2);
  margin-bottom: 20px;
}

.primary-section .selected-definition-info,
.primary-section .analysis-results {
  background: rgba(255,255,255,0.95);
  padding: 20px;
  border-radius: 12px;
  box-shadow: 0 4px 16px rgba(0,0,0,0.1);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255,255,255,0.2);
  margin-bottom: 20px;
}

/* Help Section - Expandable Cards */
.help-section {
  margin-top: 40px;
  margin-bottom: 30px;
}

.expandable-card {
  background: white;
  border-radius: 12px;
  margin-bottom: 15px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.08);
  border: 1px solid #e9ecef;
  overflow: hidden;
  transition: all 0.3s ease;
}

.expandable-card:hover {
  box-shadow: 0 4px 16px rgba(0,0,0,0.12);
  border-color: #3498db;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 25px;
  cursor: pointer;
  background: #f8f9fa;
  border-bottom: 1px solid #e9ecef;
  transition: background-color 0.2s;
}

.card-header:hover {
  background: #e9ecef;
}

.card-header h3 {
  margin: 0;
  color: #2c3e50;
  font-size: 1.1rem;
  flex-grow: 1;
}

.expand-btn {
  background: none;
  border: none;
  color: #3498db;
  font-size: 1.2rem;
  cursor: pointer;
  padding: 5px;
  border-radius: 4px;
  transition: all 0.2s;
  margin-left: 15px;
}

.expand-btn:hover {
  background: rgba(52, 152, 219, 0.1);
  color: #2980b9;
}

.expand-btn.expanded {
  color: #e74c3c;
}

.expandable-content {
  padding: 25px;
  border-top: 1px solid #f1f3f4;
  background: white;
}

.controls {
  display: flex;
  gap: 15px;
  margin-bottom: 20px;
  align-items: center;
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 15px;
  margin-bottom: 25px;
}

.stat-card {
  background: white;
  padding: 20px;
  border-radius: 8px;
  text-align: center;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.stat-card h3 {
  font-size: 2em;
  margin: 0 0 5px 0;
  color: #3498db;
}

.stat-card.need-enhancement h3 {
  color: #e74c3c;
}

.stat-card.already-enhanced h3 {
  color: #27ae60;
}

.requirements-section {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
}

.requirement-list {
  max-height: 400px;
  overflow-y: auto;
}

.requirement-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px;
  margin-bottom: 10px;
  border-radius: 6px;
  background: white;
  box-shadow: 0 1px 3px rgba(0,0,0,0.1);
}

.requirement-item.needs-work {
  border-left: 4px solid #e74c3c;
}

.requirement-item.enhanced {
  border-left: 4px solid #27ae60;
}

.requirement-item.ready-to-apply {
  background: #f0f8ff;
  border-left: 4px solid #3498db;
}

.status-badge {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  margin-left: 8px;
}

.status-badge.needs_enhancement {
  background: #fff3cd;
  color: #856404;
}

.status-badge.ready_to_apply {
  background: #cce7ff;
  color: #004085;
}

.status-badge.enhanced {
  background: #d4edda;
  color: #155724;
}

.status-badge.failed {
  background: #f8d7da;
  color: #721c24;
}

.status-badge.processing {
  background: #e2e3e5;
  color: #383d41;
}

.enhancement-info {
  display: block;
  color: #6c757d;
  font-style: italic;
  margin-top: 4px;
}

.status-text.applied {
  color: #28a745;
  font-weight: 600;
  padding: 4px 8px;
}

.req-info strong {
  color: #2c3e50;
  font-size: 14px;
}

.req-info p {
  margin: 5px 0;
  color: #5a6c7d;
  font-size: 13px;
}

.req-info small {
  color: #95a5a6;
  font-size: 11px;
}

.type-badge {
  background: #ecf0f1;
  color: #2c3e50;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 11px;
  margin-left: 10px;
}

.req-actions {
  display: flex;
  gap: 8px;
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0,0,0,0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-content {
  background: white;
  border-radius: 12px;
  max-width: 90vw;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 10px 30px rgba(0,0,0,0.3);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  border-bottom: 1px solid #ecf0f1;
}

.unsaved-indicator {
  color: #e74c3c;
  font-size: 1.2em;
  margin-left: 8px;
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0% { opacity: 1; }
  50% { opacity: 0.5; }
  100% { opacity: 1; }
}

.modal-body {
  padding: 20px;
}

.check-definition-controls {
  display: flex;
  gap: 10px;
  align-items: center;
}

.check-definition-controls select {
  flex: 1;
}

.copy-source-info {
  margin-bottom: 20px;
  padding: 15px;
  background: #f8f9fa;
  border-radius: 8px;
}

.source-definition-card {
  background: white;
  padding: 15px;
  border-radius: 6px;
  border: 1px solid #e9ecef;
}

.source-definition-card strong {
  color: #2c3e50;
  font-size: 1.1em;
}

.source-definition-card p {
  margin: 8px 0;
  color: #6c757d;
}

.definition-stats {
  display: flex;
  gap: 15px;
  margin-top: 10px;
}

.definition-stats .stat {
  background: #e9ecef;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 0.9em;
  color: #495057;
}

.copy-form {
  margin-top: 20px;
}

.copy-preview {
  margin-top: 20px;
  padding: 15px;
  background: #f0f8ff;
  border-radius: 8px;
  border: 1px solid #b3d9ff;
}

.copy-preview h5 {
  margin-bottom: 10px;
  color: #0066cc;
}

.copy-preview ul {
  margin: 10px 0;
  padding-left: 20px;
}

.copy-preview li {
  margin-bottom: 5px;
  color: #495057;
}

.copy-preview .note {
  margin-top: 15px;
  padding: 10px;
  background: #fff3cd;
  border: 1px solid #ffeaa7;
  border-radius: 4px;
  font-size: 0.9em;
}

/* Create Definition Modal Styles */
.create-definition-modal {
  max-width: 600px;
}

.modal-description {
  color: #6c757d;
  margin-bottom: 20px;
  line-height: 1.5;
}

.create-definition-form {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.create-definition-form .form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

.create-definition-form .form-group {
  margin-bottom: 0;
}

.create-definition-form label {
  display: block;
  font-weight: 600;
  margin-bottom: 6px;
  color: #374151;
}

.create-definition-form .form-hint {
  display: block;
  margin-top: 4px;
  font-size: 0.85em;
  color: #6b7280;
}

.improvement-stats {
  display: flex;
  gap: 20px;
  margin-bottom: 20px;
  padding: 15px;
  background: #f8f9fa;
  border-radius: 6px;
}

.stat label {
  font-weight: bold;
  margin-right: 8px;
}

.highlight {
  color: #27ae60;
  font-weight: bold;
}

.prompt-comparison {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
}

.prompt-text {
  background: #f8f9fa;
  padding: 15px;
  border-radius: 6px;
  font-family: 'Courier New', monospace;
  font-size: 12px;
  line-height: 1.4;
  max-height: 300px;
  overflow-y: auto;
  white-space: pre-wrap;
}

.prompt-text.enhanced {
  background: #e8f5e8;
  border-left: 4px solid #27ae60;
}

.modal-footer {
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  padding: 20px;
  border-top: 1px solid #ecf0f1;
}

.bulk-actions {
  background: #fff;
  padding: 20px;
  border-radius: 8px;
  border: 2px dashed #bdc3c7;
}

.bulk-controls {
  display: flex;
  gap: 15px;
  flex-wrap: wrap;
}

.message {
  padding: 15px;
  border-radius: 6px;
  margin-top: 20px;
  text-align: center;
}

.message.success {
  background: #d4edda;
  color: #155724;
  border: 1px solid #c3e6cb;
}

.message.error {
  background: #f8d7da;
  color: #721c24;
  border: 1px solid #f5c6cb;
}

.message.info {
  background: #d1ecf1;
  color: #0c5460;
  border: 1px solid #bee5eb;
}

/* Button Styles */
.btn {
  padding: 8px 16px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.2s;
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-primary {
  background: #3498db;
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background: #2980b9;
}

.btn-success {
  background: #27ae60;
  color: white;
}

.btn-success:hover:not(:disabled) {
  background: #229954;
}

.btn-warning {
  background: #f39c12;
  color: white;
}

.btn-warning:hover:not(:disabled) {
  background: #e67e22;
}

.btn-outline {
  background: white;
  color: #3498db;
  border: 1px solid #3498db;
}

.btn-outline:hover:not(:disabled) {
  background: #3498db;
  color: white;
}

.btn-secondary {
  background: #95a5a6;
  color: white;
}

.btn-sm {
  padding: 4px 8px;
  font-size: 12px;
}

.btn-close {
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #95a5a6;
}

.form-control {
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
}

.text-warning {
  color: #e67e22;
}

.text-success {
  color: #27ae60;
}

/* Quick Create Section Styles */
.quick-create-section {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 16px;
  padding: 30px;
  margin-bottom: 30px;
  box-shadow: 0 8px 24px rgba(0,0,0,0.15);
}

.quick-create-header {
  text-align: center;
  margin-bottom: 25px;
}

.quick-create-header h3 {
  color: white;
  font-size: 1.5rem;
  margin-bottom: 8px;
}

.quick-create-header p {
  color: rgba(255,255,255,0.9);
  font-size: 1rem;
}

.quick-create-collapsed {
  text-align: center;
}

.quick-create-collapsed .btn {
  background: rgba(255,255,255,0.95);
  color: #667eea;
  border: none;
  padding: 15px 30px;
  font-size: 1.1rem;
  font-weight: 600;
  margin-bottom: 10px;
  box-shadow: 0 4px 16px rgba(0,0,0,0.1);
}

.quick-create-collapsed .btn:hover {
  background: white;
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(0,0,0,0.15);
}

.quick-tip {
  color: rgba(255,255,255,0.8);
  font-size: 0.9rem;
  font-style: italic;
  display: block;
}

.quick-create-form {
  background: rgba(255,255,255,0.95);
  padding: 25px;
  border-radius: 12px;
  box-shadow: 0 4px 16px rgba(0,0,0,0.1);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255,255,255,0.2);
}

.form-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  margin-bottom: 20px;
}

.form-group.full-width {
  grid-column: 1 / -1;
}

.quick-create-form .form-group label {
  display: block;
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 8px;
  font-size: 0.9rem;
}

.quick-create-form .form-group .form-control {
  width: 100%;
  padding: 12px;
  border: 2px solid #e9ecef;
  border-radius: 8px;
  font-size: 0.95rem;
  transition: border-color 0.2s, box-shadow 0.2s;
}

.quick-create-form .form-group .form-control:focus {
  outline: none;
  border-color: #667eea;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

.form-hint {
  display: block;
  color: #6c757d;
  font-size: 0.8rem;
  margin-top: 5px;
  font-style: italic;
}

.quick-actions {
  display: flex;
  gap: 12px;
  justify-content: center;
  margin-top: 25px;
}

.quick-actions .btn {
  padding: 12px 24px;
  font-weight: 600;
  border-radius: 8px;
  transition: all 0.2s;
  border: none;
}

.quick-actions .btn-success {
  background: #28a745;
  color: white;
}

.quick-actions .btn-primary {
  background: #667eea;
  color: white;
}

.quick-actions .btn-outline {
  background: transparent;
  color: #6c757d;
  border: 2px solid #dee2e6;
}

.quick-actions .btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
}

.quick-actions .btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
}

/* Feedback Analysis Section Styles */
.feedback-analysis-section {
  margin-top: 30px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 20px;
  border-radius: 12px;
  color: white;
}

.feedback-analysis-section .section-header h3 {
  color: white;
  margin-bottom: 0.5rem;
}

.feedback-analysis-section .section-description {
  color: rgba(255, 255, 255, 0.9);
  margin-bottom: 1.5rem;
}

.feedback-stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin-bottom: 2rem;
}

.feedback-stat-card {
  background: rgba(255, 255, 255, 0.1);
  padding: 1.5rem;
  border-radius: 8px;
  text-align: center;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.feedback-stat-card h4 {
  font-size: 2rem;
  margin-bottom: 0.5rem;
  color: #ffd700;
}

.feedback-stat-card p {
  margin: 0;
  color: rgba(255, 255, 255, 0.9);
  font-weight: 500;
}

.feedback-stat-card.needs-improvement h4 {
  color: #ff6b6b;
}

.feedback-stat-card.enhanced h4 {
  color: #51cf66;
}

.top-issues-section {
  margin-bottom: 2rem;
}

.top-issues-section h4 {
  color: white;
  margin-bottom: 1rem;
}

.issues-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 1rem;
}

.issue-card {
  background: rgba(255, 255, 255, 0.1);
  padding: 1rem;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.issue-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
}

.issue-name {
  font-weight: 600;
  color: white;
}

.issue-count {
  background: rgba(255, 255, 255, 0.2);
  padding: 0.25rem 0.5rem;
  border-radius: 12px;
  font-size: 0.8rem;
  color: white;
}

.issue-description {
  color: rgba(255, 255, 255, 0.8);
  font-size: 0.9rem;
  line-height: 1.4;
}

.improvement-opportunities h4 {
  color: white;
  margin-bottom: 1rem;
}

.opportunities-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.opportunity-card {
  background: rgba(255, 255, 255, 0.1);
  padding: 1.5rem;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.opportunity-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

.opportunity-header h5 {
  color: white;
  margin: 0;
}

.feedback-count {
  background: rgba(255, 255, 255, 0.2);
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  font-size: 0.8rem;
  color: white;
}

.opportunity-issues {
  margin-bottom: 1rem;
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.issue-tag {
  background: rgba(255, 255, 255, 0.2);
  padding: 0.25rem 0.5rem;
  border-radius: 12px;
  font-size: 0.75rem;
  color: white;
}

.opportunity-actions {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.opportunity-actions .btn {
  font-size: 0.85rem;
}

/* NEW Styles for Manual Prompt Management */
.manual-prompt-section {
  margin-top: 30px;
  background: #f8f9fa;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  border-left: 4px solid #3498db;
}

.section-header {
  text-align: center;
  margin-bottom: 20px;
}

.section-header h2 {
  color: #2c3e50;
  margin-bottom: 10px;
}

.section-description {
  color: #7f8c8d;
  font-size: 16px;
  max-width: 800px;
  margin: 0 auto 20px auto;
}

.manual-controls {
  display: flex;
  gap: 15px;
  justify-content: center;
  flex-wrap: wrap;
  margin-bottom: 20px;
}

.prompt-library {
  background: white;
  padding: 20px;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  border-left: 4px solid #27ae60;
}

.library-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 15px;
  border-bottom: 1px solid #ecf0f1;
}

.library-header h3 {
  color: #2c3e50;
  margin: 0;
}

.library-controls {
  display: flex;
  gap: 20px;
  align-items: center;
}

.library-filters {
  display: flex;
  gap: 15px;
  align-items: center;
}

.library-filters .form-control {
  min-width: 150px;
}

.inactive-toggle {
  display: flex;
  align-items: center;
  gap: 8px;
  white-space: nowrap;
}

.view-toggle {
  display: flex;
  align-items: center;
}

.toggle-buttons {
  display: flex;
  gap: 5px;
  background: #f8f9fa;
  padding: 3px;
  border-radius: 6px;
  border: 1px solid #e9ecef;
}

.toggle-buttons .btn {
  border: none;
  background: transparent;
  color: #6c757d;
  font-size: 0.85rem;
  padding: 6px 12px;
  border-radius: 4px;
  transition: all 0.2s ease;
}

.toggle-buttons .btn:hover {
  background: #e9ecef;
  color: #495057;
}

.toggle-buttons .btn-primary {
  background: #3498db !important;
  color: white !important;
  box-shadow: 0 2px 4px rgba(52, 152, 219, 0.3);
}

.toggle-buttons .btn i {
  margin-right: 5px;
}

.library-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 20px;
}

.prompt-card {
  background: #fdfdfd;
  padding: 15px;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.05);
  border: 1px solid #eee;
  transition: transform 0.2s, box-shadow 0.2s;
}

.prompt-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 4px 10px rgba(0,0,0,0.1);
}

.prompt-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
}

.prompt-header h4 {
  color: #2c3e50;
  margin: 0;
  font-size: 16px;
}

.prompt-badges {
  display: flex;
  gap: 8px;
}

.enhancement-badge {
  background: #e0f2f7;
  color: #2980b9;
  padding: 4px 10px;
  border-radius: 15px;
  font-size: 12px;
  font-weight: bold;
}

.check-definition-badge {
  background: #e8f5e8;
  color: #2e7d32;
  padding: 4px 10px;
  border-radius: 15px;
  font-size: 12px;
  font-weight: bold;
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.severity-badge {
  background: #fdf6e3;
  color: #f39c12;
  padding: 4px 10px;
  border-radius: 15px;
  font-size: 12px;
  font-weight: bold;
}

.prompt-description {
  color: #5a6c7d;
  font-size: 13px;
  line-height: 1.5;
  margin-bottom: 10px;
}

.prompt-stats {
  display: flex;
  gap: 15px;
  font-size: 12px;
  color: #7f8c8d;
  margin-bottom: 10px;
}

.prompt-actions {
  display: flex;
  gap: 8px;
  justify-content: flex-end;
}

.prompt-editor-modal .modal-content {
  max-width: 800px;
  max-height: 90vh;
}

.prompt-editor-form {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.form-section {
  background: #f8f9fa;
  padding: 20px;
  border-radius: 8px;
  box-shadow: inset 0 1px 3px rgba(0,0,0,0.05);
}

.form-section h4 {
  color: #2c3e50;
  margin-bottom: 15px;
  font-size: 16px;
}

.form-row {
  display: flex;
  gap: 15px;
  margin-bottom: 15px;
}

.form-group {
  flex: 1;
}

.form-group label {
  display: block;
  margin-bottom: 5px;
  color: #5a6c7d;
  font-size: 13px;
}

.form-control:focus {
  border-color: #3498db;
  box-shadow: 0 0 0 0.2rem rgba(52, 152, 219, 0.25);
}

.prompt-textarea {
  font-family: 'Courier New', monospace;
  font-size: 14px;
  line-height: 1.6;
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 6px;
  resize: vertical;
  min-height: 150px;
  white-space: pre-wrap;
  word-wrap: break-word;
}

.prompt-stats-inline {
  display: flex;
  justify-content: space-between;
  font-size: 12px;
  color: #7f8c8d;
  margin-top: 10px;
}

.template-tools {
  display: flex;
  gap: 10px;
  margin-bottom: 15px;
}

.template-tools .btn {
  flex: 1;
}

.new-prompt-options {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 15px;
  margin-top: 20px;
}

.option-card {
  background: #f0f8ff;
  padding: 20px;
  border-radius: 10px;
  text-align: center;
  cursor: pointer;
  transition: transform 0.2s, box-shadow 0.2s;
  border: 1px dashed #3498db;
}

.option-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 4px 10px rgba(0,0,0,0.1);
  border-color: #2980b9;
}

.option-icon {
  font-size: 3em;
  margin-bottom: 10px;
  color: #3498db;
}

.option-card h4 {
  color: #2c3e50;
  margin-bottom: 8px;
}

.option-card p {
  color: #7f8c8d;
  font-size: 13px;
  line-height: 1.4;
}

.template-library {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 20px;
}

.template-card {
  background: #fdfdfd;
  padding: 15px;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.05);
  border: 1px solid #eee;
  transition: transform 0.2s, box-shadow 0.2s;
}

.template-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 4px 10px rgba(0,0,0,0.1);
}

.template-card h4 {
  color: #2c3e50;
  margin-bottom: 8px;
}

.template-card p {
  color: #5a6c7d;
  font-size: 13px;
  line-height: 1.4;
  margin-bottom: 10px;
}

.template-preview {
  background: #f8f9fa;
  padding: 10px;
  border-radius: 6px;
  border: 1px solid #eee;
  overflow-x: auto;
  font-size: 12px;
  line-height: 1.4;
  white-space: pre-wrap;
  word-wrap: break-word;
}

 .template-preview pre {
   margin: 0;
   padding: 0;
   white-space: pre-wrap;
   word-wrap: break-word;
 }

 /* NEW Styles for Effectiveness Tracking */
 .effectiveness-section {
   margin-top: 30px;
   background: #f0f8ff;
   padding: 20px;
   border-radius: 8px;
   box-shadow: 0 2px 8px rgba(0,0,0,0.1);
   border-left: 4px solid #2196f3;
 }

 .effectiveness-controls {
   display: flex;
   gap: 15px;
   align-items: center;
   margin-bottom: 20px;
 }

 .effectiveness-controls .form-control {
   max-width: 200px;
 }

 .effectiveness-results {
   background: white;
   padding: 20px;
   border-radius: 12px;
   box-shadow: 0 2px 8px rgba(0,0,0,0.1);
 }

 .effectiveness-summary h3 {
   color: #2c3e50;
   margin-bottom: 10px;
 }

 .recommendations-section {
   margin: 20px 0;
   padding: 15px;
   background: #fff9c4;
   border-radius: 8px;
   border-left: 4px solid #ffc107;
 }

 .recommendation-card {
   background: white;
   padding: 15px;
   margin: 10px 0;
   border-radius: 6px;
   border: 1px solid #ffc107;
 }

 .rec-header {
   display: flex;
   align-items: center;
   gap: 10px;
   margin-bottom: 8px;
 }

 .priority-badge {
   padding: 4px 8px;
   border-radius: 12px;
   font-size: 11px;
   font-weight: bold;
   text-transform: uppercase;
 }

 .priority-badge.high {
   background: #ffebee;
   color: #c62828;
 }

 .priority-badge.medium {
   background: #fff3e0;
   color: #ef6c00;
 }

 .priority-badge.low {
   background: #e8f5e8;
   color: #2e7d32;
 }

 .rec-reason {
   color: #5a6c7d;
   font-size: 14px;
   margin: 5px 0;
 }

 .rec-action {
   color: #2c3e50;
   font-weight: 500;
   margin: 5px 0;
 }

 .metrics-grid {
   display: grid;
   grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
   gap: 20px;
   margin-top: 20px;
 }

 .metric-card {
   background: #fdfdfd;
   padding: 20px;
   border-radius: 8px;
   border: 1px solid #e0e0e0;
   box-shadow: 0 2px 4px rgba(0,0,0,0.05);
 }

 .metric-header {
   display: flex;
   justify-content: space-between;
   align-items: center;
   margin-bottom: 15px;
   padding-bottom: 10px;
   border-bottom: 1px solid #eee;
 }

 .metric-header h4 {
   color: #2c3e50;
   margin: 0;
   font-size: 16px;
 }

 .requirement-code {
   background: #e3f2fd;
   color: #1976d2;
   padding: 4px 8px;
   border-radius: 4px;
   font-size: 12px;
   font-family: monospace;
 }

 .comparison-grid {
   display: grid;
   grid-template-columns: 1fr 1fr;
   gap: 15px;
   margin-bottom: 15px;
 }

 .version-metrics {
   padding: 15px;
   border-radius: 6px;
   border: 1px solid #ddd;
 }

 .version-metrics.original {
   background: #f9f9f9;
   border-left: 3px solid #757575;
 }

 .version-metrics.enhanced {
   background: #f0fff0;
   border-left: 3px solid #4caf50;
 }

 .version-metrics h5 {
   margin: 0 0 10px 0;
   font-size: 14px;
   color: #2c3e50;
 }

 .metric-item {
   display: flex;
   justify-content: space-between;
   margin: 5px 0;
   font-size: 13px;
 }

 .metric-label {
   color: #5a6c7d;
 }

 .metric-value {
   font-weight: 600;
   color: #2c3e50;
 }

 .improvement-indicators {
   display: flex;
   gap: 15px;
   padding: 10px;
   background: #f8f9fa;
   border-radius: 6px;
 }

 .improvement-item {
   display: flex;
   flex-direction: column;
   align-items: center;
   padding: 8px;
   border-radius: 4px;
   flex: 1;
 }

 .improvement-item.positive {
   background: #e8f5e8;
   color: #2e7d32;
 }

 .improvement-item.negative {
   background: #ffebee;
   color: #c62828;
 }

 .improvement-label {
   font-size: 12px;
   margin-bottom: 4px;
 }

 .improvement-value {
   font-weight: bold;
   font-size: 14px;
 }

 /* Active Toggle Styles */
 .active-toggle {
   display: flex;
   align-items: center;
   gap: 8px;
   margin-left: auto;
 }

 .toggle-switch {
   position: relative;
   display: inline-block;
   width: 44px;
   height: 24px;
 }

 .toggle-switch input {
   opacity: 0;
   width: 0;
   height: 0;
 }

 .toggle-slider {
   position: absolute;
   cursor: pointer;
   top: 0;
   left: 0;
   right: 0;
   bottom: 0;
   background-color: #ccc;
   transition: .4s;
   border-radius: 24px;
 }

 .toggle-slider:before {
   position: absolute;
   content: "";
   height: 18px;
   width: 18px;
   left: 3px;
   bottom: 3px;
   background-color: white;
   transition: .4s;
   border-radius: 50%;
 }

 input:checked + .toggle-slider {
   background-color: #28a745;
 }

 input:focus + .toggle-slider {
   box-shadow: 0 0 1px #28a745;
 }

 input:checked + .toggle-slider:before {
   transform: translateX(20px);
 }

 input:disabled + .toggle-slider {
   opacity: 0.5;
   cursor: not-allowed;
 }

 .toggle-label {
   font-size: 12px;
   font-weight: 500;
   color: #666;
 }

 .prompt-badges {
   display: flex;
   align-items: center;
   gap: 8px;
   flex-wrap: wrap;
 }

 /* Inactive prompt styling */
 .prompt-card.inactive {
   opacity: 0.7;
   background-color: #f8f9fa;
   border-left: 4px solid #dc3545;
   position: relative;
 }

 .prompt-card.inactive h4 {
   color: #6c757d;
 }

.prompt-card.inactive .prompt-description {
  color: #6c757d;
}

.prompt-card.inactive::before {
  content: 'INACTIVE';
  position: absolute;
  top: 10px;
  right: 10px;
  background: #dc3545;
  color: white;
  padding: 2px 6px;
  border-radius: 3px;
  font-size: 10px;
  font-weight: bold;
  z-index: 1;
}

/* Bulk Prompt Creation Modal Styles */
.bulk-modal-overlay {
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(2px);
}

.bulk-prompt-modal {
  width: 95%;
  max-width: 1400px;
  max-height: 95vh;
  background: white;
  border-radius: 16px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.bulk-modal-title {
  flex: 1;
}

.bulk-modal-title h3 {
  margin: 0;
  color: #2c3e50;
  font-size: 1.5rem;
}

.bulk-modal-subtitle {
  margin: 5px 0 0 0;
  color: #7f8c8d;
  font-size: 0.9rem;
}

.bulk-modal-controls {
  display: flex;
  gap: 8px;
  align-items: center;
}

.bulk-modal-body {
  flex: 1;
  overflow-y: auto;
  padding: 20px;
}

.bulk-modal-footer {
  background: #f8f9fa;
  border-top: 1px solid #e9ecef;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px 20px;
}

.footer-left .help-text {
  color: #7f8c8d;
  font-size: 0.85rem;
}

/* Progress Section */
.bulk-progress {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 15px;
  margin-bottom: 20px;
  border: 1px solid #e9ecef;
}

.progress-info {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
}

.progress-text {
  font-weight: 600;
  color: #2c3e50;
}

.progress-bar {
  height: 8px;
  background: #e9ecef;
  border-radius: 4px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #3498db, #27ae60);
  transition: width 0.3s ease;
}

/* Bulk Input Section */
.bulk-input-section {
  margin-bottom: 25px;
}

.bulk-input-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
}

.bulk-input-header h4 {
  margin: 0;
  color: #2c3e50;
}

.input-format-toggle {
  display: flex;
  align-items: center;
  gap: 10px;
}

.toggle-label {
  font-size: 0.9rem;
  color: #7f8c8d;
  font-weight: 500;
}

.bulk-textarea-container {
  position: relative;
}

.bulk-textarea {
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  font-size: 14px;
  line-height: 1.5;
  resize: vertical;
  min-height: 300px;
  background: #fafbfc;
  border: 2px dashed #bdc3c7;
  transition: border-color 0.3s ease;
}

.bulk-textarea:focus {
  border-color: #3498db;
  border-style: solid;
  background: white;
  box-shadow: 0 0 0 3px rgba(52, 152, 219, 0.1);
}

.bulk-textarea-tools {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 10px;
  padding: 8px 12px;
  background: #f8f9fa;
  border-radius: 6px;
  font-size: 0.85rem;
}

.textarea-stats {
  display: flex;
  gap: 15px;
  color: #7f8c8d;
}

.textarea-actions {
  display: flex;
  gap: 8px;
}

/* Parsed Prompts Section */
.parsed-prompts-section, .bulk-queue-section {
  margin-bottom: 25px;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
  padding-bottom: 10px;
  border-bottom: 2px solid #e9ecef;
}

.section-header h4 {
  margin: 0;
  color: #2c3e50;
}

.section-actions {
  display: flex;
  gap: 8px;
}

.parsed-prompts-list {
  max-height: 300px;
  overflow-y: auto;
  border: 1px solid #e9ecef;
  border-radius: 8px;
}

.parsed-prompt-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 15px;
  border-bottom: 1px solid #f0f0f0;
  transition: background-color 0.2s ease;
}

.parsed-prompt-item:hover {
  background: #f8f9fa;
}

.parsed-prompt-item:last-child {
  border-bottom: none;
}

.prompt-preview {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 10px;
}

.prompt-number {
  color: #3498db;
  font-weight: 600;
  min-width: 30px;
}

.prompt-text {
  color: #2c3e50;
  line-height: 1.4;
}

/* Bulk Global Settings */
.bulk-global-settings {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 15px;
  margin-bottom: 20px;
  border: 1px solid #e9ecef;
}

.settings-row {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 15px;
}

.settings-row .form-group {
  margin: 0;
}

.settings-row .form-group label {
  font-size: 0.9rem;
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 5px;
  display: block;
}

/* Bulk Prompts List */
.bulk-prompts-list {
  max-height: 500px;
  overflow-y: auto;
  border: 1px solid #e9ecef;
  border-radius: 8px;
}

.bulk-prompt-item {
  border-bottom: 1px solid #f0f0f0;
  transition: all 0.3s ease;
}

.bulk-prompt-item:last-child {
  border-bottom: none;
}

.bulk-prompt-item.processing {
  background: #fff3cd;
  border-left: 4px solid #ffc107;
}

.bulk-prompt-item.completed {
  background: #d4edda;
  border-left: 4px solid #28a745;
}

.bulk-prompt-item.error {
  background: #f8d7da;
  border-left: 4px solid #dc3545;
}

.prompt-item-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px;
  cursor: pointer;
}

.prompt-item-title {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 10px;
}

.prompt-name-input {
  flex: 1;
  border: 1px solid #ddd;
  border-radius: 4px;
  padding: 8px 12px;
  font-size: 0.9rem;
}

.prompt-item-actions {
  display: flex;
  gap: 5px;
}

.prompt-item-body {
  padding: 0 15px;
  overflow: hidden;
  transition: all 0.3s ease;
}

.prompt-item-body:not(.expanded) {
  max-height: 0;
  padding-top: 0;
  padding-bottom: 0;
}

.prompt-item-body.expanded {
  max-height: 1000px;
  padding-bottom: 15px;
}

.prompt-preview-text {
  background: #f8f9fa;
  padding: 10px;
  border-radius: 4px;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  font-size: 0.85rem;
  color: #2c3e50;
  line-height: 1.4;
  margin-bottom: 15px;
  border-left: 3px solid #3498db;
}

.prompt-item-details .form-row {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 15px;
  margin-bottom: 15px;
}

.prompt-item-status {
  margin-top: 10px;
  padding: 8px 12px;
  border-radius: 4px;
  font-size: 0.9rem;
  font-weight: 500;
}

.status-processing {
  background: #fff3cd;
  color: #856404;
}

.status-completed {
  background: #d4edda;
  color: #155724;
}

.status-error {
  background: #f8d7da;
  color: #721c24;
}

/* Minimized Modal */
.minimized-bulk-modal {
  position: fixed;
  bottom: 20px;
  right: 20px;
  background: #3498db;
  color: white;
  padding: 12px 20px;
  border-radius: 25px;
  box-shadow: 0 4px 12px rgba(52, 152, 219, 0.4);
  cursor: pointer;
  transition: all 0.3s ease;
  z-index: 1001;
}

.minimized-bulk-modal:hover {
  background: #2980b9;
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(52, 152, 219, 0.5);
}

.minimized-content {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 500;
}

/* Responsive Design */
@media (max-width: 768px) {
  .bulk-prompt-modal {
    width: 98%;
    max-height: 98vh;
  }
  
  .bulk-modal-body {
    padding: 15px;
  }
  
  .settings-row {
    grid-template-columns: 1fr;
    gap: 10px;
  }
  
  .prompt-item-details .form-row {
    grid-template-columns: 1fr;
    gap: 10px;
  }
  
  .bulk-input-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }
  
  .section-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }
  
  .minimized-bulk-modal {
    bottom: 10px;
    right: 10px;
    padding: 10px 15px;
  }
}

/* Form Control Improvements */
.form-control-sm {
  padding: 4px 8px;
  font-size: 0.875rem;
  border-radius: 4px;
}

.btn-sm {
  padding: 4px 8px;
  font-size: 0.875rem;
  border-radius: 4px;
}

/* Animation for progress */
@keyframes pulse {
  0% { opacity: 1; }
  50% { opacity: 0.5; }
  100% { opacity: 1; }
}

.bulk-prompt-item.processing .prompt-item-header {
  animation: pulse 2s infinite;
}

/* Table View Styles */
.library-table {
  background: white;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.table-container {
  overflow-x: auto;
}

.prompt-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.9rem;
}

.prompt-table th {
  background: #f8f9fa;
  color: #495057;
  font-weight: 600;
  padding: 12px 8px;
  text-align: left;
  border-bottom: 2px solid #dee2e6;
  white-space: nowrap;
  position: sticky;
  top: 0;
  z-index: 10;
}

.prompt-table th.sortable {
  cursor: pointer;
  user-select: none;
  transition: background-color 0.2s ease;
}

.prompt-table th.sortable:hover {
  background: #e9ecef;
}

.prompt-table th i {
  margin-left: 5px;
  font-size: 0.8rem;
}

.prompt-table td {
  padding: 12px 8px;
  border-bottom: 1px solid #dee2e6;
  vertical-align: top;
}

.prompt-row {
  transition: background-color 0.2s ease;
}

.prompt-row:hover {
  background: #f8f9fa;
}

.prompt-row.inactive {
  opacity: 0.7;
  background: #f8f9fa;
  border-left: 4px solid #dc3545;
}

.prompt-row.inactive td {
  color: #6c757d;
}

.prompt-row.enhanced {
  border-left: 4px solid #28a745;
}

.name-cell {
  min-width: 200px;
  max-width: 250px;
}

.name-content strong {
  display: block;
  color: #2c3e50;
  font-size: 0.95rem;
  margin-bottom: 2px;
}

.code-text {
  color: #6c757d;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  font-size: 0.8rem;
}

.definition-cell {
  min-width: 150px;
}

.check-definition-badge.table-badge {
  background: #e3f2fd;
  color: #1976d2;
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 0.8rem;
  font-weight: 500;
}

.type-cell {
  min-width: 120px;
}

.type-badge {
  background: #f3e5f5;
  color: #7b1fa2;
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 0.8rem;
  font-weight: 500;
}

.severity-cell {
  min-width: 100px;
}

.severity-badge.table-severity {
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 0.8rem;
  font-weight: 500;
  text-transform: uppercase;
}

.severity-badge.low {
  background: #e8f5e8;
  color: #2e7d32;
}

.severity-badge.medium {
  background: #fff3e0;
  color: #f57c00;
}

.severity-badge.high {
  background: #ffebee;
  color: #d32f2f;
}

.severity-badge.critical {
  background: #fce4ec;
  color: #c2185b;
}

.description-cell {
  min-width: 250px;
  max-width: 350px;
}

.description-content {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.description-text {
  color: #495057;
  line-height: 1.4;
}

.prompt-preview {
  background: #f8f9fa;
  padding: 6px 8px;
  border-radius: 4px;
  border-left: 3px solid #3498db;
}

.prompt-preview small {
  color: #6c757d;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  font-size: 0.75rem;
  line-height: 1.3;
}

.length-cell {
  min-width: 80px;
  text-align: center;
}

.length-info {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.length-chars {
  font-weight: 600;
  color: #495057;
}

.length-label {
  color: #6c757d;
  font-size: 0.75rem;
}

.status-cell {
  min-width: 100px;
}

.enhancement-badge.table-enhanced {
  background: #d4edda;
  color: #155724;
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 0.8rem;
  font-weight: 500;
}

.basic-badge {
  background: #f8f9fa;
  color: #6c757d;
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 0.8rem;
  font-weight: 500;
}

.active-cell {
  min-width: 80px;
  text-align: center;
}

.table-toggle {
  display: flex;
  justify-content: center;
}

.toggle-switch.compact {
  width: 36px;
  height: 20px;
}

.toggle-switch.compact .toggle-slider {
  border-radius: 20px;
}

.toggle-switch.compact .toggle-slider:before {
  height: 14px;
  width: 14px;
  left: 3px;
  bottom: 3px;
}

.toggle-switch.compact input:checked + .toggle-slider:before {
  transform: translateX(16px);
}

.actions-cell {
  min-width: 140px;
}

.actions-column {
  text-align: center;
}

.table-actions {
  display: flex;
  gap: 4px;
  justify-content: center;
  flex-wrap: wrap;
}

.btn-xs {
  padding: 4px 8px;
  font-size: 0.75rem;
  border-radius: 4px;
  min-width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.btn-xs i {
  font-size: 0.7rem;
}

/* Expanded Row Styles */
.expanded-row {
  background: #f8f9fa;
}

.expanded-content {
  padding: 20px;
}

.expanded-details {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.detail-section h5 {
  color: #2c3e50;
  margin: 0 0 10px 0;
  font-size: 1rem;
  font-weight: 600;
}

.detail-section p {
  margin: 0;
  color: #495057;
  line-height: 1.5;
}

.template-preview-full {
  background: white;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  padding: 15px;
  overflow-x: auto;
}

.template-preview-full pre {
  margin: 0;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  font-size: 0.85rem;
  line-height: 1.4;
  color: #495057;
  white-space: pre-wrap;
  word-break: break-word;
}

.validation-info {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.validation-item {
  color: #495057;
}

.validation-item strong {
  color: #2c3e50;
}

/* Responsive Table */
@media (max-width: 1200px) {
  .description-cell {
    max-width: 200px;
  }
  
  .name-cell {
    max-width: 150px;
  }
}

@media (max-width: 768px) {
  .library-controls {
    flex-direction: column;
    gap: 15px;
    align-items: stretch;
  }
  
  .library-filters {
    flex-direction: column;
    gap: 10px;
  }
  
  .view-toggle {
    justify-content: center;
  }
  
  .prompt-table th,
  .prompt-table td {
    padding: 8px 4px;
  }
  
  .table-actions {
    flex-direction: column;
    gap: 2px;
  }
  
  .btn-xs {
    min-width: 24px;
    height: 24px;
    padding: 2px 4px;
  }
}

/* Input Mode Toggle */
.input-mode-section {
  margin-bottom: 20px;
  padding: 15px;
  background: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #e9ecef;
}

.mode-toggle-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.mode-toggle-header h4 {
  margin: 0;
  color: #2c3e50;
}

.mode-toggle-buttons {
  display: flex;
  gap: 8px;
}

/* Structured Input Table */
.structured-input-section {
  margin-bottom: 25px;
}

.structured-input-header {
  margin-bottom: 15px;
}

.structured-input-header h4 {
  margin: 0 0 5px 0;
  color: #2c3e50;
}

.help-text {
  margin: 0;
  color: #7f8c8d;
  font-size: 0.9rem;
}

.structured-table-container {
  overflow-x: auto;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  margin-bottom: 15px;
}

.structured-input-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.9rem;
}

.structured-input-table th {
  background: #f8f9fa;
  padding: 12px 8px;
  border-bottom: 2px solid #e9ecef;
  font-weight: 600;
  color: #2c3e50;
  text-align: left;
  white-space: nowrap;
}

.structured-input-table td {
  padding: 8px;
  border-bottom: 1px solid #f0f0f0;
  vertical-align: top;
}

.structured-row:hover {
  background: #f8f9fa;
}

.structured-input-table input,
.structured-input-table textarea,
.structured-input-table select {
  width: 100%;
  min-width: 120px;
  border: 1px solid #ddd;
  border-radius: 4px;
  padding: 4px 8px;
  font-size: 0.85rem;
}

.structured-input-table textarea {
  min-height: 40px;
  resize: vertical;
}

.structured-actions {
  display: flex;
  gap: 10px;
  align-items: center;
}

/* CSV Import Modal */
.csv-import-modal {
  width: 90%;
  max-width: 1200px;
  max-height: 90vh;
}

.import-step {
  min-height: 400px;
}

.step-header {
  margin-bottom: 25px;
  padding-bottom: 15px;
  border-bottom: 2px solid #e9ecef;
}

.step-header h4 {
  margin: 0 0 8px 0;
  color: #2c3e50;
  font-size: 1.3rem;
}

.step-actions {
  margin-top: 30px;
  padding-top: 20px;
  border-top: 1px solid #e9ecef;
  display: flex;
  gap: 10px;
  justify-content: flex-end;
}

/* File Upload Section */
.file-upload-section {
  margin-bottom: 30px;
}

.upload-area {
  border: 2px dashed #bdc3c7;
  border-radius: 12px;
  padding: 40px 20px;
  text-align: center;
  background: #fafbfc;
  transition: all 0.3s ease;
  cursor: pointer;
}

.upload-area:hover,
.upload-area.dragover {
  border-color: #3498db;
  background: #f0f8ff;
}

.upload-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 15px;
}

.upload-icon {
  font-size: 3rem;
  color: #3498db;
}

.upload-content h5 {
  margin: 0;
  color: #2c3e50;
  font-size: 1.2rem;
}

.upload-content p {
  margin: 0;
  color: #7f8c8d;
  font-size: 0.9rem;
}

.file-input-hidden {
  display: none;
}

.file-info {
  margin-top: 20px;
  padding: 15px;
  background: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #e9ecef;
}

.file-details {
  display: flex;
  align-items: center;
  gap: 15px;
}

.file-details i {
  font-size: 1.5rem;
  color: #3498db;
}

.file-meta {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.file-meta strong {
  color: #2c3e50;
  font-size: 1rem;
}

.file-meta span {
  color: #7f8c8d;
  font-size: 0.85rem;
}

/* Format Info */
.format-info {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 20px;
  border: 1px solid #e9ecef;
}

.format-info h5 {
  margin: 0 0 10px 0;
  color: #2c3e50;
}

.format-info p {
  margin: 0 0 15px 0;
  color: #7f8c8d;
}

.expected-columns {
  margin-top: 15px;
}

.column-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 10px;
  margin-bottom: 15px;
}

.column-item {
  padding: 10px;
  background: white;
  border-radius: 6px;
  border: 1px solid #e9ecef;
  font-size: 0.85rem;
}

.column-item.required {
  border-left: 4px solid #e74c3c;
}

.column-item strong {
  color: #2c3e50;
  display: block;
  margin-bottom: 4px;
}

.format-note {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #7f8c8d;
  font-size: 0.85rem;
}

/* Column Mapping */
.column-mapping {
  margin-bottom: 20px;
}

.mapping-table .table {
  background: white;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.mapping-table th {
  background: #f8f9fa;
  font-weight: 600;
  color: #2c3e50;
  border-bottom: 2px solid #e9ecef;
}

.field-description {
  display: block;
  color: #7f8c8d;
  font-size: 0.8rem;
  font-weight: normal;
  margin-top: 2px;
}

.sample-data {
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  font-size: 0.8rem;
  color: #2c3e50;
  background: #f8f9fa;
  padding: 2px 6px;
  border-radius: 3px;
}

.no-data {
  color: #bdc3c7;
  font-style: italic;
  font-size: 0.8rem;
}

.badge {
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 500;
}

.badge-danger {
  background: #dc3545;
  color: white;
}

.badge-secondary {
  background: #6c757d;
  color: white;
}

.badge-success {
  background: #28a745;
  color: white;
}

/* Import Preview */
.import-preview {
  margin-bottom: 20px;
}

.preview-stats {
  display: flex;
  gap: 20px;
  margin-bottom: 20px;
}

.stat-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 15px;
  background: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #e9ecef;
}

.stat-item.error {
  background: #f8d7da;
  border-color: #dc3545;
}

.stat-item strong {
  font-size: 1.5rem;
  color: #2c3e50;
}

.stat-item.error strong {
  color: #dc3545;
}

.stat-item span {
  color: #7f8c8d;
  font-size: 0.85rem;
  margin-top: 4px;
}

.error-summary {
  background: #f8d7da;
  border: 1px solid #dc3545;
  border-radius: 8px;
  padding: 15px;
  margin-bottom: 20px;
}

.error-summary h5 {
  margin: 0 0 10px 0;
  color: #dc3545;
}

.error-list {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.error-item {
  font-size: 0.9rem;
  color: #721c24;
}

.more-errors {
  color: #7f8c8d;
  font-style: italic;
  font-size: 0.85rem;
}

.preview-table {
  background: white;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.preview-table h5 {
  margin: 0;
  padding: 15px;
  background: #f8f9fa;
  border-bottom: 1px solid #e9ecef;
  color: #2c3e50;
}

.table-sm th,
.table-sm td {
  padding: 8px 12px;
  font-size: 0.85rem;
}

/* Import Results */
.import-results {
  text-align: center;
}

.results-summary {
  display: flex;
  justify-content: center;
  gap: 30px;
  margin-bottom: 30px;
}

.result-stat {
  display: flex;
  align-items: center;
  gap: 15px;
  padding: 20px;
  background: #f8f9fa;
  border-radius: 12px;
  border: 1px solid #e9ecef;
}

.result-stat.success {
  background: #d4edda;
  border-color: #28a745;
}

.result-stat.error {
  background: #f8d7da;
  border-color: #dc3545;
}

.result-stat i {
  font-size: 2rem;
}

.result-stat.success i {
  color: #28a745;
}

.result-stat.error i {
  color: #dc3545;
}

.result-stat div strong {
  display: block;
  font-size: 1.5rem;
  color: #2c3e50;
}

.result-stat div span {
  color: #7f8c8d;
  font-size: 0.9rem;
}

.error-details {
  background: #f8d7da;
  border: 1px solid #dc3545;
  border-radius: 8px;
  padding: 20px;
  text-align: left;
}

.error-details h5 {
  margin: 0 0 15px 0;
  color: #dc3545;
}

/* Responsive Design for Import */
@media (max-width: 768px) {
  .csv-import-modal {
    width: 98%;
    max-height: 98vh;
  }
  
  .column-grid {
    grid-template-columns: 1fr;
  }
  
  .preview-stats {
    flex-direction: column;
    gap: 10px;
  }
  
  .results-summary {
    flex-direction: column;
    gap: 15px;
  }
  
  .structured-table-container {
    font-size: 0.8rem;
  }
  
  .structured-input-table th,
  .structured-input-table td {
    padding: 6px 4px;
  }
  
  .structured-actions {
    flex-wrap: wrap;
  }
}
</style>